import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { apiLogin, apiRegister, apiGetProfile, apiSaveProfile, apiBattleSave, apiListProfiles, apiCreateProfile, apiDeleteProfile } from './api';

// ==================== PROFILE & SAVE SYSTEM (legado — chave mantida para compatibilidade) ====================
const STORAGE_KEY = 'gramatica-quest-profiles'; // eslint-disable-line no-unused-vars

// Load all profiles from localStorage
const loadProfiles = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error loading profiles:', error);
    return {};
  }
};

// Save profile to localStorage (legado — não mais usado após migração para API)
const saveProfile = (profileName, gameData) => { // eslint-disable-line no-unused-vars
  try {
    const profiles = loadProfiles();
    profiles[profileName] = {
      ...gameData,
      lastPlayed: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
  } catch (error) {
    console.error('Error saving profile:', error);
  }
};

// Delete profile from localStorage (legado)
const deleteProfile = (profileName) => { // eslint-disable-line no-unused-vars
  try {
    const profiles = loadProfiles();
    delete profiles[profileName];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
  } catch (error) {
    console.error('Error deleting profile:', error);
  }
};

// ==================== GAME DATA ====================
const TITLES = [
  { level: 1, xp: 0, name: "Aprendiz de Palavras", emoji: "📜" },
  { level: 2, xp: 200, name: "Escriba Iniciante", emoji: "✒️" },
  { level: 3, xp: 500, name: "Guerreiro Gramatical", emoji: "⚔️" },
  { level: 4, xp: 1000, name: "Cavaleiro da Sintaxe", emoji: "🛡️" },
  { level: 5, xp: 2000, name: "Mago dos Verbos", emoji: "🔮" },
  { level: 6, xp: 3500, name: "Sábio da Concordância", emoji: "📚" },
  { level: 7, xp: 5500, name: "Arquimago Linguístico", emoji: "🌟" },
  { level: 8, xp: 8000, name: "Mestre da Gramática", emoji: "👑" },
  { level: 9, xp: 11000, name: "Lenda Portuguesa", emoji: "🏆" },
  { level: 10, xp: 15000, name: "GRAMÁTICA IMORTAL", emoji: "⚡" },
];

const PHASES = [
  { id: 1, name: "Vila das Palavras", subtitle: "Classes de Palavras", icon: "🏰", color: "#10b981", unlockLevel: 1 },
  { id: 2, name: "Floresta dos Verbos", subtitle: "Verbos & Conjugação", icon: "🌲", color: "#22c55e", unlockLevel: 2 },
  { id: 3, name: "Montanha das Frases", subtitle: "Estrutura Sintática", icon: "⛰️", color: "#3b82f6", unlockLevel: 3 },
  { id: 4, name: "Deserto da Concordância", subtitle: "Concordância", icon: "🏜️", color: "#f59e0b", unlockLevel: 4 },
  { id: 5, name: "Caverna da Regência", subtitle: "Regência & Crase", icon: "🕳️", color: "#8b5cf6", unlockLevel: 5 },
  { id: 6, name: "Castelo da Pontuação", subtitle: "Pontuação", icon: "🏯", color: "#ec4899", unlockLevel: 6 },
  { id: 7, name: "Torre do Mestre", subtitle: "Domínio Total", icon: "🗼", color: "#ef4444", unlockLevel: 7 },
];

const ENEMIES = {
  slime: { name: "Slime da Confusão", hp: 5, emoji: "👹", damagedEmoji: "😤", desperateEmoji: "😱", xpReward: 50 },
  zombie: { name: "Zumbi do Erro", hp: 10, emoji: "🧟", damagedEmoji: "🤕", desperateEmoji: "💀", xpReward: 100 },
  dragon: { name: "Dragão da Dúvida", hp: 20, emoji: "🐉", damagedEmoji: "🐲", desperateEmoji: "🔥", xpReward: 200 },
  boss: { name: "Boss de Fase", hp: 50, emoji: "👑", damagedEmoji: "👿", desperateEmoji: "😡", xpReward: 500 },
};

// eslint-disable-next-line no-unused-vars
const BADGES = [
  { id: "hunter", name: "Caçador de Palavras", emoji: "🎯", description: "Completou primeira batalha" },
  { id: "verbs", name: "Domador de Verbos", emoji: "⚔️", description: "Dominou verbos básicos" },
  { id: "master", name: "Mestre das Palavras", emoji: "📖", description: "Completou Fase 1" },
  { id: "streak7", name: "Chama Eterna", emoji: "🔥", description: "7 dias seguidos" },
  { id: "perfect", name: "Perfeição", emoji: "💎", description: "100% em uma fase" },
  { id: "speed", name: "Relâmpago", emoji: "⚡", description: "Resposta em menos de 5s" },
  { id: "conjugator", name: "Conjugador Mestre", emoji: "🏆", description: "Dominou conjugação" },
  { id: "syntax", name: "Arquiteto de Frases", emoji: "🏗️", description: "Completou sintaxe" },
  { id: "harmony", name: "Mestre da Harmonia", emoji: "🎵", description: "Dominou concordância" },
  { id: "crase", name: "Domador da Crase", emoji: "À", description: "Venceu o terror da crase" },
];

// Questions database organized by phase
const QUESTIONS = {
  1: [ // Vila das Palavras - Classes de Palavras
    { q: "Na frase 'O cachorro grande late', qual palavra é o SUBSTANTIVO?", options: ["O", "cachorro", "grande", "late"], answer: 1, xp: 25, difficulty: "basic", topic: "Substantivos" },
    { q: "Na frase 'A casa bonita foi vendida', qual palavra é o ADJETIVO?", options: ["casa", "bonita", "foi", "vendida"], answer: 1, xp: 25, difficulty: "basic", topic: "Adjetivos" },
    { q: "Classifique a palavra 'Maria':", options: ["Verbo", "Adjetivo", "Substantivo próprio", "Advérbio"], answer: 2, xp: 25, difficulty: "basic", topic: "Substantivos" },
    { q: "Classifique a palavra 'feliz':", options: ["Substantivo", "Adjetivo", "Verbo", "Pronome"], answer: 1, xp: 25, difficulty: "basic", topic: "Adjetivos" },
    { q: "Na frase 'Ele corre rapidamente', qual é o ADVÉRBIO?", options: ["Ele", "corre", "rapidamente", "Nenhum"], answer: 2, xp: 30, difficulty: "intermediate", topic: "Advérbios" },
    { q: "Complete: 'Vou ____ casa' (preposição correta)", options: ["a", "em", "para", "Todas corretas"], answer: 3, xp: 30, difficulty: "intermediate", topic: "Preposições" },
    { q: "Identifique a CONJUNÇÃO: 'Estudei e passei'", options: ["Estudei", "e", "passei", "Não há"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Conjunções" },
    { q: "Qual é a INTERJEIÇÃO na frase: 'Uau! Que legal!'?", options: ["Que", "legal", "Uau", "Não há"], answer: 2, xp: 30, difficulty: "intermediate", topic: "Interjeições" },
    { q: "Complete com o ARTIGO correto: '____ casa é bonita'", options: ["O", "A", "Um", "Uma"], answer: 1, xp: 25, difficulty: "basic", topic: "Artigos" },
    { q: "Substitua por PRONOME: 'Maria e João foram ao cinema'", options: ["Ele", "Ela", "Eles", "Nós"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Pronomes" },
    { q: "Classifique 'nós':", options: ["Substantivo", "Adjetivo", "Pronome pessoal", "Artigo"], answer: 2, xp: 25, difficulty: "basic", topic: "Pronomes" },
    { q: "Identifique o NUMERAL: 'Comprei dois livros'", options: ["Comprei", "dois", "livros", "Não há"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Numerais" },
    { q: "Na frase 'Livro de Maria', qual é a PREPOSIÇÃO?", options: ["Livro", "de", "Maria", "Não há"], answer: 1, xp: 25, difficulty: "basic", topic: "Preposições" },
    { q: "Classifique 'longe' em 'Ele mora longe':", options: ["Adjetivo", "Advérbio", "Substantivo", "Preposição"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Advérbios" },
    { q: "'Queria ir, mas estava cansado'. A palavra 'mas' é:", options: ["Conjunção aditiva", "Conjunção adversativa", "Preposição", "Advérbio"], answer: 1, xp: 40, difficulty: "advanced", topic: "Conjunções" },
    { q: "Qual classe de palavra indica AÇÃO?", options: ["Substantivo", "Adjetivo", "Verbo", "Advérbio"], answer: 2, xp: 25, difficulty: "basic", topic: "Classes de Palavras" },
    { q: "Na frase 'Este livro é meu', 'meu' é:", options: ["Artigo", "Pronome possessivo", "Adjetivo", "Substantivo"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Pronomes" },
    { q: "Identifique todos os ARTIGOS: 'Um menino viu a lua'", options: ["Um, a", "menino, lua", "viu", "Não há artigos"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Artigos" },
    { q: "'Porque' é classificado como:", options: ["Advérbio", "Preposição", "Conjunção", "Interjeição"], answer: 2, xp: 30, difficulty: "intermediate", topic: "Conjunções" },
    { q: "Quantas classes de palavras existem na língua portuguesa?", options: ["8", "10", "12", "6"], answer: 1, xp: 25, difficulty: "basic", topic: "Classes de Palavras" },
  ],
  2: [ // Floresta dos Verbos
    { q: "Qual é o INFINITIVO do verbo 'comeu'?", options: ["come", "comer", "comendo", "comido"], answer: 1, xp: 25, difficulty: "basic", topic: "Verbos - Formas Nominais" },
    { q: "Complete: 'Eu ____ muito feliz' (estar - presente)", options: ["estou", "está", "estar", "estive"], answer: 0, xp: 25, difficulty: "basic", topic: "Conjugação Verbal" },
    { q: "Complete: 'Eles ____ futebol' (jogar - presente)", options: ["joga", "jogam", "jogar", "jogou"], answer: 1, xp: 25, difficulty: "basic", topic: "Conjugação Verbal" },
    { q: "Identifique o VERBO: 'Maria canta bem'", options: ["Maria", "canta", "bem", "Não há"], answer: 1, xp: 25, difficulty: "basic", topic: "Verbos - Identificação" },
    { q: "Conjugue 'falar' na 1ª pessoa do singular (presente):", options: ["falo", "falas", "fala", "falamos"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Conjugação Verbal" },
    { q: "Qual verbo está no INFINITIVO?", options: ["corro", "correr", "corri", "correndo"], answer: 1, xp: 25, difficulty: "basic", topic: "Verbos - Formas Nominais" },
    { q: "'Amou' está em qual tempo verbal?", options: ["Presente", "Pretérito perfeito", "Futuro", "Infinitivo"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Tempos Verbais" },
    { q: "Transforme para o passado: 'Eu como pizza'", options: ["Eu comi pizza", "Eu comerei pizza", "Eu como pizza", "Eu comia pizza"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Tempos Verbais" },
    { q: "Conjugue 'ser' na 3ª pessoa do plural (presente):", options: ["somos", "são", "é", "sou"], answer: 1, xp: 40, difficulty: "advanced", topic: "Verbos Irregulares" },
    { q: "'Farei' está em qual tempo verbal?", options: ["Presente", "Passado", "Futuro", "Infinitivo"], answer: 2, xp: 30, difficulty: "intermediate", topic: "Tempos Verbais" },
    { q: "Qual é o verbo IRREGULAR?", options: ["amar", "comer", "ser", "partir"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Verbos Irregulares" },
    { q: "Complete: 'Nós ____ ao cinema ontem' (ir)", options: ["vamos", "fomos", "iremos", "íamos"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Verbos Irregulares" },
    { q: "'Tinha estudado' é qual tempo composto?", options: ["Pretérito mais-que-perfeito", "Futuro do pretérito", "Presente", "Pretérito perfeito"], answer: 0, xp: 50, difficulty: "advanced", topic: "Tempos Verbais" },
    { q: "Identifique a forma verbal CONJUGADA:", options: ["cantar", "cantando", "cantou", "cantar"], answer: 2, xp: 25, difficulty: "basic", topic: "Verbos - Identificação" },
    { q: "O verbo 'haver' no presente (3ª singular) é:", options: ["há", "houve", "havia", "haverá"], answer: 0, xp: 40, difficulty: "advanced", topic: "Verbos Irregulares" },
    { q: "Qual é a 1ª conjugação (-AR)?", options: ["beber", "partir", "amar", "dormir"], answer: 2, xp: 25, difficulty: "basic", topic: "Conjugação Verbal" },
    { q: "'Eu teria ido' está em:", options: ["Futuro do pretérito composto", "Pretérito perfeito", "Presente", "Infinitivo"], answer: 0, xp: 50, difficulty: "advanced", topic: "Tempos Verbais" },
    { q: "Complete: 'Se eu ____ rico' (ser - pretérito imperfeito do subjuntivo)", options: ["sou", "fosse", "for", "seria"], answer: 1, xp: 50, difficulty: "advanced", topic: "Modo Subjuntivo" },
    { q: "Conjugue 'ter' na 1ª pessoa do singular (presente):", options: ["tenho", "tem", "tinha", "tive"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Verbos Irregulares" },
    { q: "O gerúndio de 'fazer' é:", options: ["feito", "fazendo", "fez", "fazer"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Verbos - Formas Nominais" },
  ],
  3: [ // Montanha das Frases - Sintaxe
    { q: "Identifique o SUJEITO: 'O menino comprou doces'", options: ["O menino", "comprou", "doces", "Não há"], answer: 0, xp: 25, difficulty: "basic", topic: "Sujeito" },
    { q: "Identifique o PREDICADO: 'Maria estuda muito'", options: ["Maria", "estuda muito", "muito", "estuda"], answer: 1, xp: 25, difficulty: "basic", topic: "Predicado" },
    { q: "Qual o tipo de sujeito em 'Choveu muito ontem'?", options: ["Simples", "Composto", "Oculto", "Inexistente"], answer: 3, xp: 40, difficulty: "advanced", topic: "Tipos de Sujeito" },
    { q: "Identifique o OBJETO DIRETO: 'Ele comprou um carro'", options: ["Ele", "comprou", "um carro", "Não há"], answer: 2, xp: 30, difficulty: "intermediate", topic: "Complementos Verbais" },
    { q: "Identifique o OBJETO INDIRETO: 'Dei o livro ao João'", options: ["o livro", "ao João", "Dei", "Não há"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Complementos Verbais" },
    { q: "'Pedro e Maria estudam' tem sujeito:", options: ["Simples", "Composto", "Oculto", "Indeterminado"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Tipos de Sujeito" },
    { q: "Na frase 'Estudamos muito', o sujeito é:", options: ["Estudamos", "muito", "Oculto (nós)", "Inexistente"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Tipos de Sujeito" },
    { q: "Identifique o ADJUNTO ADVERBIAL: 'Ele chegou cedo'", options: ["Ele", "chegou", "cedo", "Não há"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Adjuntos" },
    { q: "'Vendem-se casas' tem sujeito:", options: ["Indeterminado", "Oculto", "casas", "Inexistente"], answer: 2, xp: 50, difficulty: "advanced", topic: "Tipos de Sujeito" },
    { q: "Qual a função de 'bonita' em 'A casa é bonita'?", options: ["Objeto direto", "Predicativo do sujeito", "Adjunto adnominal", "Sujeito"], answer: 1, xp: 40, difficulty: "advanced", topic: "Predicativo" },
    { q: "Identifique o ADJUNTO ADNOMINAL: 'O livro novo chegou'", options: ["O", "livro", "novo", "chegou"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Adjuntos" },
    { q: "'Preciso de ajuda'. 'de ajuda' é:", options: ["Objeto direto", "Objeto indireto", "Complemento nominal", "Adjunto adverbial"], answer: 1, xp: 40, difficulty: "advanced", topic: "Complementos Verbais" },
    { q: "Em 'Faz dez anos', qual é o sujeito?", options: ["Faz", "dez anos", "Oculto", "Inexistente"], answer: 3, xp: 50, difficulty: "advanced", topic: "Tipos de Sujeito" },
    { q: "Identifique o VOCATIVO: 'João, venha aqui!'", options: ["João", "venha", "aqui", "Não há"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Vocativo e Aposto" },
    { q: "'O pai, herói da família, chegou'. 'herói da família' é:", options: ["Sujeito", "Aposto", "Vocativo", "Predicativo"], answer: 1, xp: 40, difficulty: "advanced", topic: "Vocativo e Aposto" },
    { q: "Qual a função de 'professor' em 'Ele é professor'?", options: ["Sujeito", "Objeto direto", "Predicativo do sujeito", "Aposto"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Predicativo" },
    { q: "'Há muitas pessoas na festa'. O sujeito é:", options: ["muitas pessoas", "na festa", "Há", "Inexistente"], answer: 3, xp: 45, difficulty: "advanced", topic: "Tipos de Sujeito" },
    { q: "Em 'Deram-lhe um presente', 'lhe' é:", options: ["Sujeito", "Objeto direto", "Objeto indireto", "Adjunto"], answer: 2, xp: 40, difficulty: "advanced", topic: "Complementos Verbais" },
    { q: "Identifique o AGENTE DA PASSIVA: 'O bolo foi feito pela mãe'", options: ["O bolo", "foi feito", "pela mãe", "Não há"], answer: 2, xp: 45, difficulty: "advanced", topic: "Voz Passiva" },
    { q: "'A certeza de vitória animou todos'. 'de vitória' é:", options: ["Adjunto adnominal", "Complemento nominal", "Objeto indireto", "Aposto"], answer: 1, xp: 50, difficulty: "advanced", topic: "Complementos Nominais" },
  ],
  4: [ // Deserto da Concordância
    { q: "Complete: 'Os meninos ____ felizes' (estar)", options: ["está", "estão", "estamos", "estou"], answer: 1, xp: 25, difficulty: "basic", topic: "Concordância Verbal" },
    { q: "Complete: 'A maioria dos alunos ____ aprovada'", options: ["foi", "foram", "Ambas corretas", "Nenhuma"], answer: 2, xp: 40, difficulty: "advanced", topic: "Concordância Verbal" },
    { q: "'Fazem dez anos' ou 'Faz dez anos'?", options: ["Fazem", "Faz", "Ambas", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Concordância Verbal" },
    { q: "Complete: 'Vossa Excelência ____ convidado'", options: ["é", "sois", "são", "és"], answer: 0, xp: 45, difficulty: "advanced", topic: "Concordância com Pronomes" },
    { q: "'Mais de um aluno ____' (comparecer - pretérito)", options: ["compareceu", "compareceram", "Ambas", "Nenhuma"], answer: 0, xp: 40, difficulty: "advanced", topic: "Concordância Verbal" },
    { q: "Complete: 'Tu e eu ____ amigos'", options: ["somos", "sois", "são", "é"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Concordância com Pronomes" },
    { q: "'Havia' ou 'Haviam' muitas pessoas?", options: ["Havia", "Haviam", "Ambas", "Nenhuma"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Concordância Verbal" },
    { q: "'Cerca de mil pessoas ____' (comparecer)", options: ["compareceu", "compareceram", "Ambas", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Concordância Verbal" },
    { q: "Complete: '50% da turma ____ aprovada'", options: ["foi", "foram", "Ambas", "Nenhuma"], answer: 2, xp: 45, difficulty: "advanced", topic: "Concordância com Numerais" },
    { q: "'Um ou outro ____' (virá/virão)?", options: ["virá", "virão", "Ambas", "Nenhuma"], answer: 0, xp: 40, difficulty: "advanced", topic: "Concordância Verbal" },
    { q: "Complete: 'Ela mesma ____ o bolo'", options: ["fez", "fizeram", "fiz", "fazem"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Concordância Verbal" },
    { q: "'É proibido' ou 'É proibida entrada'?", options: ["proibido", "proibida", "Ambas possíveis", "Nenhuma"], answer: 2, xp: 50, difficulty: "advanced", topic: "Concordância Nominal" },
    { q: "Complete: 'Os Estados Unidos ____ grande'", options: ["é", "são", "Ambas", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Concordância Verbal" },
    { q: "'Bastante' ou 'Bastantes pessoas'?", options: ["Bastante", "Bastantes", "Ambas", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Concordância Nominal" },
    { q: "Complete: 'Menos ____ são mais'", options: ["palavras", "palavra", "Ambas", "Nenhuma"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Concordância Nominal" },
    { q: "'Anexo' ou 'Anexa as fotos'?", options: ["Anexo", "Anexa", "Anexas", "Anexos"], answer: 2, xp: 40, difficulty: "advanced", topic: "Concordância Nominal" },
    { q: "'Meio-dia e ____'", options: ["meio", "meia", "Ambas", "Nenhuma"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Concordância Nominal" },
    { q: "Complete: 'Aluga-se ____'", options: ["casa", "casas", "Ambas (muda o verbo)", "Nenhuma"], answer: 2, xp: 45, difficulty: "advanced", topic: "Concordância Verbal" },
    { q: "'Obrigado' ou 'Obrigada' (mulher falando)?", options: ["Obrigado", "Obrigada", "Ambas", "Nenhuma"], answer: 1, xp: 25, difficulty: "basic", topic: "Concordância Nominal" },
    { q: "'Houve' ou 'Houveram problemas'?", options: ["Houve", "Houveram", "Ambas", "Nenhuma"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Concordância Verbal" },
  ],
  5: [ // Caverna da Regência e Crase
    { q: "'Assistir ____ filme' (preposição correta)", options: ["o", "ao", "no", "Nenhuma"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Regência Verbal" },
    { q: "'Cheguei ____ escola'", options: ["na", "à", "em a", "a"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Crase" },
    { q: "'Visei ____ cargo' (aspirar)", options: ["o", "ao", "no", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Regência Verbal" },
    { q: "Usa-se crase antes de:", options: ["Palavras masculinas", "Verbos", "Palavras femininas com artigo", "Pronomes pessoais"], answer: 2, xp: 25, difficulty: "basic", topic: "Crase" },
    { q: "'Vou ____ São Paulo'", options: ["a", "à", "Ambas", "Nenhuma"], answer: 0, xp: 40, difficulty: "advanced", topic: "Crase" },
    { q: "'Refiro-me ____ questão'", options: ["a", "à", "a a", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Crase" },
    { q: "'Vou ____ pé'", options: ["a", "à", "ao", "Nenhuma"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Crase" },
    { q: "'Chegou ____ uma hora' (horário)", options: ["a", "à", "as", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Crase" },
    { q: "'Prefiro café ____ chá'", options: ["do que", "a", "que", "à"], answer: 1, xp: 40, difficulty: "advanced", topic: "Regência Verbal" },
    { q: "'Obedecer ____ leis'", options: ["as", "às", "a", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Regência Verbal" },
    { q: "Há crase em 'à medida que'?", options: ["Sim, sempre", "Não, nunca", "Depende", "Opcional"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Crase" },
    { q: "'Fui ____ Bahia'", options: ["a", "à", "Ambas", "Nenhuma"], answer: 1, xp: 40, difficulty: "advanced", topic: "Crase" },
    { q: "'____ partir de hoje'", options: ["A", "À", "Ambas", "Nenhuma"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Crase" },
    { q: "'Aspirar' (desejar) pede:", options: ["Objeto direto", "Objeto indireto (a)", "Complemento nominal", "Nenhum"], answer: 1, xp: 40, difficulty: "advanced", topic: "Regência Verbal" },
    { q: "'Ele namora ____ Maria' (regência culta)", options: ["com", "a", "Nenhuma preposição", "de"], answer: 2, xp: 45, difficulty: "advanced", topic: "Regência Verbal" },
    { q: "'Informei ____ alunos ____ prova'", options: ["os, da", "aos, a", "os, sobre a", "Todas corretas"], answer: 3, xp: 50, difficulty: "advanced", topic: "Regência Verbal" },
    { q: "'Vou ____ casa de Maria'", options: ["a", "à", "na", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Crase" },
    { q: "'Escrevi ____ lápis'", options: ["a", "à", "de", "com"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Regência Verbal" },
    { q: "'Paguei ____ funcionário'", options: ["o", "ao", "para o", "Todas corretas"], answer: 1, xp: 40, difficulty: "advanced", topic: "Regência Verbal" },
    { q: "'Ela cheira ____ rosas'", options: ["a", "às", "as", "Nenhuma"], answer: 0, xp: 45, difficulty: "advanced", topic: "Regência Verbal" },
  ],
  6: [ // Castelo da Pontuação
    { q: "Quando usamos vírgula antes de 'e'?", options: ["Sempre", "Nunca", "Quando muda o sujeito", "Opcional"], answer: 2, xp: 35, difficulty: "intermediate", topic: "Vírgula" },
    { q: "'Maria porém não veio'. A pontuação correta é:", options: ["Maria porém, não veio", "Maria, porém, não veio", "Maria; porém não veio", "Correta assim"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Vírgula" },
    { q: "Usa-se dois-pontos antes de:", options: ["Conjunção", "Enumeração ou explicação", "Sujeito", "Verbo"], answer: 1, xp: 25, difficulty: "basic", topic: "Dois-pontos" },
    { q: "O ponto e vírgula separa:", options: ["Orações curtas", "Itens de enumeração complexa", "Sujeito e predicado", "Vocativo"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Ponto e Vírgula" },
    { q: "'João o melhor aluno passou'. Falta:", options: ["Ponto final", "Vírgulas no aposto", "Dois-pontos", "Nada"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Vírgula" },
    { q: "Travessão é usado para:", options: ["Diálogo ou destaque", "Separar sujeito", "Finalizar frase", "Ligar palavras"], answer: 0, xp: 25, difficulty: "basic", topic: "Travessão e Aspas" },
    { q: "'Ele disse que viria'. Precisa de vírgula?", options: ["Sim, antes de 'que'", "Não", "Sim, depois de 'disse'", "Opcional"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Vírgula" },
    { q: "Aspas são usadas para:", options: ["Citações e ironia", "Separar orações", "Indicar pausa", "Finalizar"], answer: 0, xp: 25, difficulty: "basic", topic: "Travessão e Aspas" },
    { q: "'Se chover não irei'. A vírgula vai:", options: ["Antes de 'Se'", "Depois de 'chover'", "Não precisa", "Depois de 'não'"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Vírgula" },
    { q: "Reticências indicam:", options: ["Fim de frase", "Suspensão ou hesitação", "Pergunta", "Exclamação"], answer: 1, xp: 25, difficulty: "basic", topic: "Outros Sinais" },
    { q: "'O menino cansado dormiu'. Vírgulas?", options: ["O menino, cansado, dormiu", "Correto assim", "Ambas possíveis", "O menino cansado, dormiu"], answer: 2, xp: 40, difficulty: "advanced", topic: "Vírgula" },
    { q: "Parênteses são usados para:", options: ["Informação adicional", "Diálogo", "Citação", "Ênfase"], answer: 0, xp: 25, difficulty: "basic", topic: "Outros Sinais" },
    { q: "'Comprei frutas maçãs laranjas e bananas'. Pontue:", options: ["Comprei frutas: maçãs, laranjas e bananas", "Comprei frutas maçãs; laranjas e bananas", "Correto assim", "Comprei: frutas maçãs laranjas, e bananas"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Dois-pontos" },
    { q: "Nunca se usa vírgula:", options: ["Antes de 'mas'", "Entre sujeito e verbo", "Após vocativo", "Em enumerações"], answer: 1, xp: 40, difficulty: "advanced", topic: "Vírgula" },
    { q: "'Estudou muito portanto passou'. Pontuação:", options: ["Estudou muito, portanto passou", "Estudou muito portanto, passou", "Estudou muito; portanto, passou", "Correto assim"], answer: 2, xp: 45, difficulty: "advanced", topic: "Ponto e Vírgula" },
    { q: "Quando o adjunto adverbial é longo no início:", options: ["Vírgula obrigatória", "Vírgula proibida", "Opcional", "Dois-pontos"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Vírgula" },
    { q: "'Amanhã disse ele viajarei'. Pontuação correta:", options: ["Amanhã, disse ele, viajarei", "Amanhã disse, ele viajarei", "'Amanhã', disse ele, 'viajarei'", "Amanhã disse ele: viajarei"], answer: 2, xp: 50, difficulty: "advanced", topic: "Travessão e Aspas" },
    { q: "Ponto de exclamação expressa:", options: ["Dúvida", "Emoção ou ordem", "Continuidade", "Explicação"], answer: 1, xp: 25, difficulty: "basic", topic: "Outros Sinais" },
    { q: "'Quero saber se você vem'. Pontuação final:", options: ["Interrogação", "Ponto final", "Exclamação", "Reticências"], answer: 1, xp: 40, difficulty: "advanced", topic: "Outros Sinais" },
    { q: "Em diálogos, cada fala nova pede:", options: ["Aspas", "Travessão e parágrafo", "Parênteses", "Vírgula"], answer: 1, xp: 30, difficulty: "intermediate", topic: "Travessão e Aspas" },
  ],
  7: [ // Torre do Mestre - Mix de tudo
    { q: "Identifique o erro: 'Haviam muitas pessoas'", options: ["Haviam → Havia", "muitas → muita", "pessoas → pessoa", "Não há erro"], answer: 0, xp: 40, difficulty: "advanced", topic: "Concordância Verbal" },
    { q: "'À medida que' ou 'Na medida em que'?", options: ["Iguais", "À medida que = proporção", "Na medida em que = proporção", "Ambas erradas"], answer: 1, xp: 50, difficulty: "advanced", topic: "Crase" },
    { q: "'Mau' ou 'Mal' em 'Ele está ___'?", options: ["mau", "mal", "Ambas", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Palavras que se Confundem" },
    { q: "Identifique: 'A moça cujo pai é médico'", options: ["Pronome relativo", "Conjunção", "Preposição", "Advérbio"], answer: 0, xp: 40, difficulty: "advanced", topic: "Pronomes" },
    { q: "'Onde' ou 'Aonde' em '____ você vai'?", options: ["Onde", "Aonde", "Ambas", "Nenhuma"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Palavras que se Confundem" },
    { q: "'Porque', 'Por que', 'Porquê' ou 'Por quê'? '____ você veio?'", options: ["Porque", "Por que", "Porquê", "Por quê"], answer: 1, xp: 40, difficulty: "advanced", topic: "Palavras que se Confundem" },
    { q: "Plural de 'cidadão':", options: ["cidadãos", "cidadões", "cidadães", "Todas corretas"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Flexão Nominal" },
    { q: "'Há' ou 'A' em '____ muito tempo'?", options: ["Há", "A", "Ambas", "À"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Palavras que se Confundem" },
    { q: "Forma correta:", options: ["Menas gente", "Menos gente", "Menas pessoas", "Menos de gente"], answer: 1, xp: 25, difficulty: "basic", topic: "Palavras que se Confundem" },
    { q: "'Se eu ver' ou 'Se eu vir'?", options: ["ver", "vir", "Ambas", "vier"], answer: 1, xp: 40, difficulty: "advanced", topic: "Verbos Irregulares" },
    { q: "Quando 'este' e quando 'esse'?", options: ["Iguais", "Este=perto, Esse=longe", "Este=longe, Esse=perto", "Aleatório"], answer: 1, xp: 35, difficulty: "intermediate", topic: "Pronomes Demonstrativos" },
    { q: "'Entrega à' ou 'Entrega a domicílio'?", options: ["à", "a", "Ambas", "Nenhuma"], answer: 1, xp: 45, difficulty: "advanced", topic: "Crase" },
    { q: "Particípio de 'imprimir' (com ter/haver):", options: ["imprimido", "impresso", "Ambas", "imprimindo"], answer: 0, xp: 45, difficulty: "advanced", topic: "Verbos Irregulares" },
    { q: "'Em vez de' ou 'Ao invés de' (substituição)?", options: ["Em vez de", "Ao invés de", "Iguais", "Nenhuma"], answer: 0, xp: 40, difficulty: "advanced", topic: "Palavras que se Confundem" },
    { q: "'A gente vai' ou 'A gente vamos'?", options: ["vai", "vamos", "Ambas", "iremos"], answer: 0, xp: 30, difficulty: "intermediate", topic: "Concordância Verbal" },
    { q: "Hífen em 'bem-vindo' é:", options: ["Obrigatório", "Proibido", "Opcional", "Depende"], answer: 0, xp: 35, difficulty: "intermediate", topic: "Hífen" },
    { q: "'Através de' significa:", options: ["Por meio de", "Atravessando", "Correto para 'por meio de'", "Ambos A e B"], answer: 3, xp: 50, difficulty: "advanced", topic: "Palavras que se Confundem" },
    { q: "'Senão' ou 'Se não' em 'Venha, ____ perderá'?", options: ["senão", "se não", "Ambas", "Nenhuma"], answer: 0, xp: 40, difficulty: "advanced", topic: "Palavras que se Confundem" },
    { q: "Certo ou errado: 'Segue anexo os documentos'", options: ["Certo", "Errado (seguem anexos)", "Errado (segue anexos)", "Errado (anexo os)"], answer: 1, xp: 45, difficulty: "advanced", topic: "Concordância Nominal" },
    { q: "Complete: 'Faz anos que não ____ vejo'", options: ["o", "lhe", "ele", "Todas corretas"], answer: 0, xp: 40, difficulty: "advanced", topic: "Pronomes" },
  ],
};

// Lessons structure - SISTEMA EDUCACIONAL PROFUNDO
const LESSONS = {
  1: { // Vila das Palavras
    intro: {
      narrator: "Mestre Léxicos, o Sábio das Palavras",
      avatar: "🧙‍♂️",
      text: (playerName) => `Saudações, ${playerName}! Eu sou Mestre Léxicos, guardião desta vila. Há muito tempo, o Reino da Linguagem estava em harmonia, mas um feitiço obscuro quebrou nossa comunicação. As palavras perderam seus significados... Você é nossa última esperança! Eu vou te ensinar os fundamentos das palavras - são como ferramentas que você usará em sua jornada. Preparado para começar sua transformação, ${playerName}?`
    },
    chapters: [
      {
        id: 1,
        name: "Capítulo 1: O que são SUBSTANTIVOS?",
        icon: "📦",
        description: "Aprenda a identificar os 'nomes' das coisas",
        pages: [
          {
            type: "narrative",
            title: "🎭 A Taverna sem Nomes",
            content: "Você entra na taverna da vila e percebe algo estranho: ninguém consegue pedir nada!\n\n'Ei, me vê aquele... aquilo... que se come!' - grita um aldeão.\n'Qual?' - responde o taverneiro confuso.\n\nO Mestre Léxicos sussurra: 'Vê? Sem SUBSTANTIVOS, não conseguimos nomear as coisas. Venha, vou te ensinar o primeiro poder da linguagem.'"
          },
          {
            type: "content",
            title: "📚 AULA 1: O Poder de Nomear",
            content: "**SUBSTANTIVOS** são palavras que dão NOME às coisas. Pense assim:\n\n**🎯 ANALOGIA SIMPLES:**\nImagine que você está em um videogame. Tudo que você pode clicar, pegar ou ver tem um nome, certo? Mesa, cadeira, espada, personagem... Esses nomes são SUBSTANTIVOS!\n\n**Na vida real:**\n- Pessoas: João, Maria, professor, médico\n- Lugares: Brasil, escola, casa, parque\n- Objetos: celular, carro, livro, cadeira\n- Sentimentos: amor, raiva, alegria, medo\n- Ideias: liberdade, educação, esperança\n\n**✨ REGRA DE OURO:**\nSe você pode colocar 'O', 'A', 'UM' ou 'UMA' antes, é substantivo!\n- O cachorro ✓\n- A felicidade ✓\n- Um livro ✓\n- Uma ideia ✓"
          },
          {
            type: "content",
            title: "🎯 TIPOS de Substantivos (Importante!)",
            content: "Existem 2 tipos principais:\n\n**⚔️ SUBSTANTIVOS COMUNS** (genéricos)\n- cachorro, cidade, mulher, computador\n- São nomes que servem para QUALQUER um daquele tipo\n- 'cachorro' pode ser qualquer cachorro do mundo\n\n**🛡️ SUBSTANTIVOS PRÓPRIOS** (específicos)\n- Rex, São Paulo, Maria, Microsoft\n- São nomes ÚNICOS, específicos\n- 'Rex' é UM cachorro específico\n- **SEMPRE começam com LETRA MAIÚSCULA!**\n\n**⚔️ EXEMPLO DE BATALHA:**\n- 'guerreiro' = comum (qualquer guerreiro)\n- 'Kratos' = próprio (aquele guerreiro específico)"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Na frase 'O cachorro late', qual palavra é o SUBSTANTIVO (o nome da coisa)?",
            options: ["O", "cachorro", "late", "Nenhuma"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'Cachorro' é o SUBSTANTIVO porque é o NOME do animal. 'O' é só um artigo (palavrinha que acompanha). 'Late' é o que ele FAZ (verbo)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual é o substantivo PRÓPRIO (nome específico)?",
            options: ["cidade", "Brasil", "país", "lugar"],
            answer: 1,
            xp: 30,
            explanation: "✅ PERFEITO! 'Brasil' é substantivo PRÓPRIO porque é o nome de UM país específico. Os outros são substantivos COMUNS (servem para qualquer cidade, país, lugar)."
          },
          {
            type: "content",
            title: "🎪 DESAFIO: Caçador de Substantivos",
            content: "Leia esta frase:\n\n'Maria foi à escola com seu cachorro Rex para estudar matemática.'\n\nVamos identificar TODOS os substantivos:\n\n1. **Maria** = substantivo PRÓPRIO (nome de pessoa específica)\n2. **escola** = substantivo COMUM (qualquer escola)\n3. **cachorro** = substantivo COMUM (qualquer cachorro)\n4. **Rex** = substantivo PRÓPRIO (nome específico do cachorro)\n5. **matemática** = substantivo COMUM (nome de uma matéria)\n\n**🔮 SABEDORIA ANCESTRAL:** Pergunta que ajuda a identificar:\n'O que tem na frase que eu posso tocar, ver ou pensar sobre?'\nSe der para responder, é substantivo!"
          },
          {
            type: "practice",
            question: "🎯 TESTE FINAL: Quantos substantivos há em 'João comprou um livro na livraria'?",
            options: ["2 substantivos", "3 substantivos", "4 substantivos", "5 substantivos"],
            answer: 1,
            xp: 50,
            explanation: "✅ EXATO! São 3 substantivos: 'João' (próprio), 'livro' (comum) e 'livraria' (comum). 'Comprou' é verbo (ação) e 'um' e 'na' são outras classes."
          },
          {
            type: "narrative",
            title: "🎉 Primeira Conquista!",
            content: "O Mestre Léxicos sorri orgulhoso:\n\n'Impressionante! Você aprendeu a identificar SUBSTANTIVOS! Agora os aldeões conseguem pedir comida na taverna novamente!'\n\nVocê ganhou o título: **NOMEADOR INICIANTE**\n\nMas a jornada continua... Agora você precisa aprender sobre ADJETIVOS - as palavras que DESCREVEM os substantivos!"
          }
        ]
      },
      {
        id: 2,
        name: "Capítulo 2: O que são ADJETIVOS?",
        icon: "🎨",
        description: "Aprenda a descrever as coisas com características",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Pintor Sem Cores",
            content: "Na praça da vila, um pintor tenta descrever suas obras:\n\n'Vendo um cavalo!' - ele grita.\n'Como é o cavalo?' - pergunta uma mulher.\n'É um cavalo... um cavalo... é... cavalo!' - ele responde frustrado.\n\nMestre Léxicos ri: 'Sem ADJETIVOS, não conseguimos DESCREVER nada! Você já sabe NOMEAR (substantivos). Agora vou te ensinar a DESCREVER!'"
          },
          {
            type: "content",
            title: "📚 AULA 2: O Poder de Descrever",
            content: "**ADJETIVOS** são palavras que DESCREVEM, CARACTERIZAM os substantivos.\n\n**🎯 ANALOGIA SIMPLES:**\nQuando você cria um personagem em um jogo, você escolhe características dele, certo?\n- Alto ou baixo?\n- Forte ou fraco?\n- Rápido ou lento?\n\nEssas características são ADJETIVOS!\n\n**SUBSTANTIVO (nome)** + **ADJETIVO (característica)**\n- cachorro GRANDE\n- casa BONITA\n- dia ENSOLARADO\n- pessoa INTELIGENTE\n\n**✨ REGRA DE OURO:**\nAdjetivos respondem a pergunta: 'COMO É?'\n- Como é o cachorro? GRANDE (adjetivo)\n- Como é a casa? BONITA (adjetivo)"
          },
          {
            type: "content",
            title: "🎯 Substantivo vs Adjetivo - A DIFERENÇA",
            content: "Entenda a diferença para NUNCA mais confundir:\n\n**SUBSTANTIVO = NOME** (quem/o que é?)\n**ADJETIVO = CARACTERÍSTICA** (como é?)\n\n📦 Pense em uma caixa:\n- A CAIXA é o substantivo\n- GRANDE, AZUL, PESADA são adjetivos\n\n**EXEMPLOS CLAROS:**\n\n1. 'O cachorro GRANDE late'\n   - cachorro = substantivo (nome do animal)\n   - grande = adjetivo (característica dele)\n\n2. 'Maria é INTELIGENTE'\n   - Maria = substantivo (nome da pessoa)\n   - inteligente = adjetivo (característica dela)\n\n3. 'Comprei um carro VERMELHO'\n   - carro = substantivo (nome do objeto)\n   - vermelho = adjetivo (cor/característica)"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Na frase 'A casa bonita', qual palavra é o ADJETIVO?",
            options: ["A", "casa", "bonita"],
            answer: 2,
            xp: 30,
            explanation: "✅ CERTO! 'Bonita' é o ADJETIVO porque descreve como é a casa. 'Casa' é o substantivo (o nome da coisa). 'A' é artigo."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual frase TEM um adjetivo?",
            options: ["João correu", "O livro novo", "Maria cantou", "Ele chegou"],
            answer: 1,
            xp: 30,
            explanation: "✅ PERFEITO! 'O livro novo' tem o adjetivo 'novo' que descreve o livro. As outras frases só têm substantivos e verbos (ações)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 3: Identifique SUBSTANTIVO e ADJETIVO em 'O carro rápido':",
            options: ["carro=substantivo, rápido=adjetivo", "carro=adjetivo, rápido=substantivo", "Ambos são substantivos", "Ambos são adjetivos"],
            answer: 0,
            xp: 40,
            explanation: "✅ PERFEITO! 'Carro' é o substantivo (nome do objeto) e 'rápido' é o adjetivo que descreve como é o carro. Sempre: SUBSTANTIVO = nome, ADJETIVO = característica!"
          },
          {
            type: "narrative",
            title: "🎉 O Pintor Recupera as Cores!",
            content: "Você ensinou os aldeões a usarem adjetivos!\n\nO pintor agora grita animado:\n'Vendo um cavalo BRANCO e VELOZ!'\n'Vendo uma casa GRANDE e CONFORTÁVEL!'\n\nMestre Léxicos sorri: 'Excelente! Você dominou substantivos E adjetivos! Mas a comunicação ainda está incompleta... Precisamos aprender sobre AÇÕES - os VERBOS!'"
          }
        ]
      },
      {
        id: 3,
        name: "Capítulo 3: O que são VERBOS?",
        icon: "⚡",
        description: "Aprenda sobre ações e estados",
        pages: [
          {
            type: "narrative",
            title: "🎭 A Estátua Viva",
            content: "Na praça central, você encontra todos os aldeões parados como estátuas!\n\n'O que houve?' - você pergunta ao Mestre Léxicos.\n\n'Eles esqueceram os VERBOS!' - ele responde. 'Sem verbos, não conseguimos expressar AÇÕES ou ESTADOS. Estão literalmente paralisados! Rápido, você precisa aprender sobre verbos para salvá-los!'"
          },
          {
            type: "content",
            title: "📚 AULA 3: O Poder de Agir",
            content: "**VERBOS** são palavras que expressam AÇÃO, ESTADO ou FENÔMENO DA NATUREZA.\n\n**🎯 ANALOGIA SIMPLES:**\nEm um videogame, o que seu personagem FAZ?\n- CORRER, PULAR, ATACAR, DEFENDER = VERBOS!\n\n**TIPOS DE VERBOS:**\n\n**1️⃣ VERBOS DE AÇÃO** (o que você FAZ):\n- correr, pular, comer, estudar, trabalhar\n- São os mais fáceis de identificar!\n\n**2️⃣ VERBOS DE ESTADO** (como você ESTÁ):\n- ser, estar, parecer, permanecer, ficar\n- Exemplo: 'Eu SOU feliz', 'Ela ESTÁ cansada'\n\n**3️⃣ FENÔMENOS DA NATUREZA:**\n- chover, nevar, ventar, amanhecer\n- Exemplo: 'Está CHOVENDO'\n\n**✨ REGRA DE OURO:**\nVerbo = PALAVRA DE AÇÃO ou ESTADO\nPergunta: 'O que está fazendo?' ou 'Como está?'\n- O que João faz? CORRE (verbo)\n- Como Maria está? ESTÁ feliz (verbo)"
          },
          {
            type: "content",
            title: "🎯 INFINITIVO - A Forma Original",
            content: "Todo verbo tem uma forma ORIGINAL chamada INFINITIVO.\n\n**É como o nome do verbo no dicionário!**\n\n**INFINITIVOS terminam em:**\n- **-AR**: andar, falar, amar, estudar\n- **-ER**: comer, beber, correr, vender  \n- **-IR**: partir, dormir, sorrir, decidir\n\n**🎮 PENSE ASSIM:**\n- INFINITIVO = nome da habilidade\n  - 'Correr', 'Pular', 'Atacar'\n- CONJUGADO = quando você USA a habilidade\n  - 'Eu corro', 'Ele pula', 'Nós atacamos'\n\n**EXEMPLOS:**\n- 'Eu preciso COMER' → COMER = infinitivo\n- 'Eu COMO pizza' → COMO = conjugado\n- 'Vou ESTUDAR' → ESTUDAR = infinitivo\n- 'Eu ESTUDO muito' → ESTUDO = conjugado"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Qual é o VERBO na frase 'Maria corre rápido'?",
            options: ["Maria", "corre", "rápido", "Não há verbo"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'Corre' é o VERBO porque expressa a AÇÃO que Maria faz. 'Maria' é substantivo (nome) e 'rápido' é advérbio (como ela corre)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual está no INFINITIVO (forma original)?",
            options: ["corro", "correr", "corri", "correndo"],
            answer: 1,
            xp: 30,
            explanation: "✅ PERFEITO! 'Correr' está no INFINITIVO (forma original, termina em -ER). 'Corro', 'corri' são conjugados. 'Correndo' é gerúndio."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 3: Qual frase tem um verbo de ESTADO (como está)?",
            options: ["João corre", "Maria é inteligente", "Pedro come", "Ana pula"],
            answer: 1,
            xp: 40,
            explanation: "✅ EXCELENTE! 'É' é verbo de ESTADO, mostra COMO Maria está/é. Os outros são verbos de AÇÃO (corre, come, pula)."
          },
          {
            type: "narrative",
            title: "🎉 Os Aldeões Voltam a Se Mover!",
            content: "Assim que você domina os verbos, os aldeões voltam a se mover!\n\n'Eu POSSO me mover!' - grita um.\n'Eu ESTOU vivo!' - comemora outro.\n\nMestre Léxicos: 'Incrível! Você devolveu o movimento à vila! Substantivos, adjetivos e verbos - você já conhece as 3 classes mais importantes! Mas ainda há mais...'"
          }
        ]
      },
      {
        id: 4,
        name: "Capítulo 4: PRONOMES - Substituir Nomes",
        icon: "🎭",
        description: "Aprenda a substituir nomes por pronomes",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Festival da Repetição",
            content: "Você chega ao festival anual e ouve:\n\n'João comprou pão. João comeu o pão. João gostou do pão de João!'\n\nTodos riem da frase estranha.\n\nMestre Léxicos explica: 'Sem PRONOMES, precisamos repetir os nomes o tempo todo! Soa ridículo, não? Vou te ensinar a usar pronomes para evitar essa repetição!'"
          },
          {
            type: "content",
            title: "📚 AULA 4: Pronomes - Os Substitutos",
            content: "**PRONOMES** são palavras que SUBSTITUEM ou ACOMPANHAM os substantivos.\n\n**🎯 ANALOGIA SIMPLES:**\nÉ como um apelido ou atalho:\n- Em vez de dizer 'João' toda hora, você diz 'ELE'\n- Em vez de 'Maria e Ana', você diz 'ELAS'\n\n**PRONOMES PESSOAIS (os principais):**\n\n**SINGULAR:**\n- EU (1ª pessoa - quem fala)\n- TU / VOCÊ (2ª pessoa - com quem fala)\n- ELE / ELA (3ª pessoa - de quem fala)\n\n**PLURAL:**\n- NÓS (1ª pessoa plural)\n- VÓS / VOCÊS (2ª pessoa plural)\n- ELES / ELAS (3ª pessoa plural)\n\n**EXEMPLO PRÁTICO:**\n❌ 'João comprou pão. João comeu o pão.'\n✅ 'João comprou pão. ELE comeu o pão.'\n\n❌ 'Maria e Ana foram ao cinema. Maria e Ana adoraram!'\n✅ 'Maria e Ana foram ao cinema. ELAS adoraram!'"
          },
          {
            type: "content",
            title: "🎯 OUTROS TIPOS DE PRONOMES",
            content: "**PRONOMES POSSESSIVOS** (mostram posse):\n- MEU, MINHA, TEU, TUA, SEU, SUA, NOSSO, NOSSA\n- Exemplo: 'Este é o MEU livro' / 'Aquela é a SUA casa'\n\n**PRONOMES DEMONSTRATIVOS** (apontam):\n- ESTE, ESSE, AQUELE (perto, médio, longe)\n- Exemplo: 'ESTE livro aqui' / 'AQUELE carro lá'\n\n**PRONOMES INTERROGATIVOS** (perguntam):\n- QUEM? QUAL? QUANTO? QUE?\n- Exemplo: 'QUEM é você?' / 'QUAL você prefere?'\n\n**🔮 SABEDORIA PRÁTICA:**\nPronome substitui nome = Para evitar repetição!"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Substitua por PRONOME: 'Maria foi ao mercado'",
            options: ["Eu fui ao mercado", "Ela foi ao mercado", "Nós fomos ao mercado", "Tu foste ao mercado"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'Maria' (mulher, 3ª pessoa singular) é substituída por 'ELA'. Se fosse 'João' seria 'ELE'."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual é o pronome possessivo em 'Este é o meu carro'?",
            options: ["Este", "é", "meu", "carro"],
            answer: 2,
            xp: 30,
            explanation: "✅ PERFEITO! 'Meu' é pronome POSSESSIVO, indica que o carro pertence a mim (posse). 'Este' é pronome demonstrativo."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 3: Substitua: 'Pedro e João jogaram bola'",
            options: ["Ele jogou bola", "Eles jogaram bola", "Nós jogamos bola", "Ela jogou bola"],
            answer: 1,
            xp: 40,
            explanation: "✅ EXCELENTE! 'Pedro e João' = 2 pessoas do gênero masculino = ELES (plural masculino)."
          }
        ]
      },
      {
        id: 5,
        name: "Capítulo 5: ARTIGOS - Acompanhantes",
        icon: "📌",
        description: "Aprenda os pequenos acompanhantes dos substantivos",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Mercado Confuso",
            content: "No mercado, um vendedor grita:\n\n'Vendo maçã! Vendo banana! Vendo laranja!'\n\nUma cliente pergunta: 'Qual maçã? UMA específica ou QUALQUER uma?'\n\nO vendedor fica confuso.\n\nMestre Léxicos: 'Vê? Sem ARTIGOS, não sabemos se é algo específico ou genérico! Vou te ensinar.'"
          },
          {
            type: "content",
            title: "📚 AULA 5: Artigos - Definindo ou Indefinindo",
            content: "**ARTIGOS** são palavrinhas que vêm ANTES dos substantivos.\n\n**🎯 ANALOGIA SIMPLES:**\nÉ como um GPS que indica se você está falando de algo:\n- ESPECÍFICO (definido): 'O carro' = aquele carro específico\n- GENÉRICO (indefinido): 'Um carro' = qualquer carro\n\n**ARTIGOS DEFINIDOS** (específico):\n- O (masculino singular): O cachorro\n- A (feminino singular): A casa\n- OS (masculino plural): Os carros\n- AS (feminino plural): As casas\n\n**ARTIGOS INDEFINIDOS** (genérico):\n- UM (masculino singular): Um cachorro (qualquer um)\n- UMA (feminino singular): Uma casa (qualquer uma)\n- UNS (masculino plural): Uns carros\n- UMAS (feminino plural): Umas casas\n\n**DIFERENÇA NA PRÁTICA:**\n- 'Comprei O carro' = carro específico que eu já conhecia\n- 'Comprei UM carro' = carro qualquer, genérico"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Qual é o ARTIGO em 'A casa é bonita'?",
            options: ["A", "casa", "é", "bonita"],
            answer: 0,
            xp: 25,
            explanation: "✅ CORRETO! 'A' é artigo DEFINIDO feminino singular. Acompanha o substantivo 'casa'."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual frase usa artigo INDEFINIDO (genérico)?",
            options: ["O livro está na mesa", "Comprei um livro", "Os meninos jogam", "A professora ensina"],
            answer: 1,
            xp: 35,
            explanation: "✅ PERFEITO! 'Um' é artigo INDEFINIDO = qualquer livro, genérico. Os outros usam artigos DEFINIDOS (O, Os, A)."
          }
        ]
      },
      {
        id: 6,
        name: "Capítulo 6: ADVÉRBIOS - Modificadores",
        icon: "🎚️",
        description: "Aprenda a modificar verbos e adjetivos",
        pages: [
          {
            type: "narrative",
            title: "🎭 A Corrida Sem Detalhes",
            content: "Na corrida anual, o narrador anuncia:\n\n'João corre! Maria corre! Pedro corre!'\n\nO público reclama: 'Mas COMO eles correm? Rápido? Devagar? Quando? Onde?'\n\nMestre Léxicos: 'Ah! Faltam os ADVÉRBIOS! Eles dão detalhes sobre COMO, QUANDO, ONDE e QUANTO as ações acontecem!'"
          },
          {
            type: "content",
            title: "📚 AULA 6: Advérbios - Os Detalhistas",
            content: "**ADVÉRBIOS** modificam VERBOS, ADJETIVOS ou outros ADVÉRBIOS.\n\n**🎯 ANALOGIA SIMPLES:**\nÉ como adicionar configurações a uma ação:\n- Corre → Corre RAPIDAMENTE (como?)\n- Corre → Corre HOJE (quando?)\n- Corre → Corre AQUI (onde?)\n\n**TIPOS DE ADVÉRBIOS:**\n\n**MODO** (como?):\n- rapidamente, devagar, bem, mal, facilmente\n- 'Ele fala RAPIDAMENTE'\n\n**TEMPO** (quando?):\n- hoje, ontem, amanhã, cedo, tarde, agora, já\n- 'Estudei ONTEM'\n\n**LUGAR** (onde?):\n- aqui, ali, lá, perto, longe, dentro, fora\n- 'Moro LONGE'\n\n**INTENSIDADE** (quanto?):\n- muito, pouco, bastante, demais, tão\n- 'Ela é MUITO inteligente'\n\n**✨ TRUQUE:**\nMaioria dos advérbios de modo terminam em -MENTE:\nrápida + MENTE = rapidamente\nlenta + MENTE = lentamente"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Identifique o ADVÉRBIO em 'Ele corre rapidamente':",
            options: ["Ele", "corre", "rapidamente", "Não há advérbio"],
            answer: 2,
            xp: 30,
            explanation: "✅ CORRETO! 'Rapidamente' é advérbio de MODO, indica COMO ele corre. Modifica o verbo 'corre'."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual tipo de advérbio é 'ontem' em 'Estudei ontem'?",
            options: ["Advérbio de lugar", "Advérbio de tempo", "Advérbio de modo", "Advérbio de intensidade"],
            answer: 1,
            xp: 35,
            explanation: "✅ PERFEITO! 'Ontem' é advérbio de TEMPO, indica QUANDO a ação aconteceu."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 3: Complete: 'Ela é ____ inteligente' (intensidade):",
            options: ["aqui", "ontem", "muito", "rapidamente"],
            answer: 2,
            xp: 30,
            explanation: "✅ EXCELENTE! 'Muito' é advérbio de INTENSIDADE, indica O QUANTO ela é inteligente."
          }
        ]
      },
      {
        id: 7,
        name: "Capítulo 7: PREPOSIÇÕES - Conectores",
        icon: "🔗",
        description: "Aprenda a ligar palavras",
        pages: [
          {
            type: "narrative",
            title: "🎭 As Palavras Desconectadas",
            content: "Na biblioteca, você vê livros escritos assim:\n\n'Livro Maria', 'Casa campo', 'Chave porta'\n\nTudo sem conexão!\n\nMestre Léxicos: 'Sem PREPOSIÇÕES, as palavras ficam soltas! São como cola que conecta as palavras. Vem, vou te mostrar!'"
          },
          {
            type: "content",
            title: "📚 AULA 7: Preposições - A Cola das Palavras",
            content: "**PREPOSIÇÕES** são palavras que LIGAM, CONECTAM outras palavras.\n\n**🎯 ANALOGIA SIMPLES:**\nSão como conectores LEGO que juntam as peças:\n- Livro DE Maria (conecta livro com Maria)\n- Casa NO campo (conecta casa com campo)\n- Fui COM você (conecta ação com pessoa)\n\n**PRINCIPAIS PREPOSIÇÕES:**\n\n**Essenciais:**\n- A, ANTE, APÓS, ATÉ, COM, CONTRA\n- DE, DESDE, EM, ENTRE, PARA, PER\n- PERANTE, POR, SEM, SOB, SOBRE, TRÁS\n\n**MAIS USADAS NO DIA A DIA:**\n- **DE** = origem, posse\n  - 'Livro DE Maria', 'Vim DE casa'\n- **EM** = lugar, tempo\n  - 'Estou EM casa', 'EM janeiro'\n- **PARA** = direção, finalidade\n  - 'Vou PARA casa', 'É PARA você'\n- **COM** = companhia\n  - 'Fui COM ela'\n- **POR** = causa, passagem\n  - 'Fiz POR você', 'Passei POR ali'\n- **A** = direção, tempo\n  - 'Vou A pé', 'Às 3 horas'"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Qual é a PREPOSIÇÃO em 'Livro de Maria'?",
            options: ["Livro", "de", "Maria", "Não há preposição"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'De' é PREPOSIÇÃO que conecta 'livro' com 'Maria', indicando posse (o livro pertence a Maria)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Complete com preposição: 'Vou ____ escola'",
            options: ["de", "em", "para", "com"],
            answer: 2,
            xp: 35,
            explanation: "✅ PERFEITO! 'PARA' indica direção, destino. 'Vou PARA a escola' = estou indo na direção da escola."
          }
        ]
      },
      {
        id: 8,
        name: "Capítulo 8: CONJUNÇÕES - Unidoras de Ideias",
        icon: "🤝",
        description: "Aprenda a unir frases e ideias",
        pages: [
          {
            type: "narrative",
            title: "🎭 As Histórias Picotadas",
            content: "O contador de histórias da vila tenta narrar:\n\n'João saiu. Maria saiu. Foram juntos. Choveu. Voltaram.'\n\nAs crianças bocejam com a história entediante.\n\nMestre Léxicos: 'Sem CONJUNÇÕES, as frases ficam soltas e chatas! Preciso te ensinar a unir ideias!'"
          },
          {
            type: "content",
            title: "📚 AULA 8: Conjunções - Juntando Frases",
            content: "**CONJUNÇÕES** conectam FRASES e IDEIAS.\n\n**🎯 ANALOGIA SIMPLES:**\nSão como pontes entre ilhas (frases):\n- 'Estudei' ➡️ E ➡️ 'Passei' = Estudei E passei\n- 'Queria ir' ➡️ MAS ➡️ 'Choveu' = Queria ir MAS choveu\n\n**TIPOS PRINCIPAIS:**\n\n**ADITIVAS** (adiciona ideia):\n- **E, NEM**\n- 'Estudei E passei', 'Não comi NEM bebi'\n\n**ADVERSATIVAS** (oposição/contraste):\n- **MAS, PORÉM, CONTUDO, TODAVIA**\n- 'Estudei MAS não passei', 'É caro PORÉM vale a pena'\n\n**ALTERNATIVAS** (escolha):\n- **OU**\n- 'Café OU chá?', 'Vai OU fica?'\n\n**CONCLUSIVAS** (conclusão):\n- **LOGO, PORTANTO, ENTÃO**\n- 'Estudei muito, LOGO passei'\n\n**EXPLICATIVAS** (explicação):\n- **PORQUE, POIS, QUE**\n- 'Estudei PORQUE queria passar'"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Qual conjunção em 'Estudei mas não passei'?",
            options: ["Estudei", "mas", "não", "passei"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'Mas' é conjunção ADVERSATIVA, mostra oposição/contraste entre estudar e não passar."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Tipo da conjunção: 'Estudei E passei'",
            options: ["Adversativa", "Aditiva", "Alternativa", "Conclusiva"],
            answer: 1,
            xp: 35,
            explanation: "✅ PERFEITO! 'E' é conjunção ADITIVA, adiciona uma ideia à outra (estudar + passar)."
          }
        ]
      },
      {
        id: 9,
        name: "Capítulo 9: INTERJEIÇÕES - Emoções",
        icon: "😮",
        description: "Aprenda a expressar emoções",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Teatro Sem Emoção",
            content: "No teatro, os atores dizem:\n\n'Ganhei na loteria' - sem emoção\n'Meu cachorro fugiu' - sem emoção\n'Que susto que levei' - sem emoção\n\nO público dorme.\n\nMestre Léxicos: 'Sem INTERJEIÇÕES, não expressamos EMOÇÕES! A comunicação fica MORTA!'"
          },
          {
            type: "content",
            title: "📚 AULA 9: Interjeições - Expressando Sentimentos",
            content: "**INTERJEIÇÕES** são palavras que expressam EMOÇÕES e REAÇÕES.\n\n**🎯 ANALOGIA SIMPLES:**\nSão como emojis falados! 😮🎉😱\n- Ver algo incrível: 'UAU!' 😮\n- Sentir dor: 'AI!' 😣\n- Ficar feliz: 'EBA!' 🎉\n\n**PRINCIPAIS INTERJEIÇÕES:**\n\n**ALEGRIA:**\n- Oba! Eba! Viva! Uhu!\n- 'Oba! Ganhei na loteria!'\n\n**DOR/LAMENTO:**\n- Ai! Ui! Ah!\n- 'Ai! Machuquei o pé!'\n\n**SURPRESA/ADMIRAÇÃO:**\n- Uau! Nossa! Caramba! Puxa!\n- 'Uau! Que legal!'\n\n**SUSTO:**\n- Opa! Epa! Socorro!\n- 'Opa! Quase caí!'\n\n**CHAMAMENTO:**\n- Ei! Oi! Olá! Psiu!\n- 'Ei! Vem cá!'\n\n**SILÊNCIO:**\n- Psiu! Silêncio!\n- 'Psiu! A aula vai começar!'\n\n**✨ CARACTERÍSTICAS:**\n- Normalmente vêm com ponto de exclamação (!)\n- Expressam emoção instantânea"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Qual é a interjeição em 'Uau! Que bonito!'?",
            options: ["Uau", "Que", "bonito", "Não há interjeição"],
            answer: 0,
            xp: 25,
            explanation: "✅ CORRETO! 'Uau!' é INTERJEIÇÃO de admiração/surpresa, expressa a emoção de ver algo bonito."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Que emoção expressa 'Ai! Machuquei o pé!'?",
            options: ["Alegria", "Dor", "Surpresa", "Silêncio"],
            answer: 1,
            xp: 30,
            explanation: "✅ PERFEITO! 'Ai!' expressa DOR/LAMENTO, reação ao se machucar."
          }
        ]
      },
      {
        id: 10,
        name: "Capítulo 10: NUMERAIS - Números",
        icon: "🔢",
        description: "Aprenda sobre números na gramática",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Mercado Sem Quantidades",
            content: "No mercado, você ouve:\n\n'Vendo maçãs!' - mas quantas?\n'Cheguei em lugar!' - qual lugar?\n'Quero parte da pizza!' - qual parte?\n\nNinguém entende nada!\n\nMestre Léxicos: 'Sem NUMERAIS, não conseguimos quantificar, ordenar ou dividir! Vamos aprender!'"
          },
          {
            type: "content",
            title: "📚 AULA 10: Numerais - Os Números da Gramática",
            content: "**NUMERAIS** são palavras relacionadas a NÚMEROS.\n\n**🎯 ANALOGIA SIMPLES:**\nSão como números em um jogo:\n- Quantidade: 'Tenho 3 vidas'\n- Ordem: 'Cheguei em 1º lugar'\n- Fração: 'Metade da pizza'\n\n**TIPOS DE NUMERAIS:**\n\n**CARDINAIS** (quantidade exata):\n- um, dois, três, cem, mil\n- 'Comprei TRÊS livros', 'Tenho VINTE reais'\n\n**ORDINAIS** (ordem, posição):\n- primeiro, segundo, terceiro, décimo\n- 'Fui o PRIMEIRO da fila', 'Moro no QUINTO andar'\n\n**MULTIPLICATIVOS** (vezes, múltiplos):\n- dobro, triplo, quádruplo\n- 'Quero o DOBRO', 'Ganhou o TRIPLO'\n\n**FRACIONÁRIOS** (partes, frações):\n- meio, metade, terço, quarto\n- 'Comi METADE da pizza', 'Um TERÇO do bolo'\n\n**DICA PRÁTICA:**\n- CARDINAL = responde 'quantos?' → TRÊS livros\n- ORDINAL = responde 'em que posição?' → TERCEIRO lugar"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Tipo de numeral em 'Comprei dois livros':",
            options: ["Ordinal", "Cardinal", "Multiplicativo", "Fracionário"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'Dois' é numeral CARDINAL, indica QUANTIDADE exata (2 livros)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Tipo de numeral em 'Cheguei em primeiro':",
            options: ["Ordinal", "Cardinal", "Multiplicativo", "Fracionário"],
            answer: 0,
            xp: 30,
            explanation: "✅ PERFEITO! 'Primeiro' é numeral ORDINAL, indica POSIÇÃO/ORDEM (1º lugar)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 3: Tipo de numeral em 'Comi metade da pizza':",
            options: ["Ordinal", "Cardinal", "Multiplicativo", "Fracionário"],
            answer: 3,
            xp: 35,
            explanation: "✅ EXCELENTE! 'Metade' é numeral FRACIONÁRIO, indica PARTE/FRAÇÃO (1/2 da pizza)."
          },
          {
            type: "narrative",
            title: "🎉 FASE 1 COMPLETA - Você é um Mestre das Palavras!",
            content: "O Mestre Léxicos está radiante de orgulho!\n\n'INCRÍVEL! Você dominou as 10 CLASSES DE PALAVRAS! A Vila das Palavras está salva! A comunicação foi restaurada!'\n\nVocê ganhou o título: **MESTRE DAS CLASSES GRAMATICAIS**\n\nMas a jornada não acaba aqui...\n\nMestre Léxicos: 'Agora você precisa ir à FLORESTA DOS VERBOS! Lá, o Guardião do Tempo te ensinará os segredos dos TEMPOS VERBAIS e da CONJUGAÇÃO. Só assim você poderá continuar sua missão de restaurar o Reino da Linguagem!'\n\n**DESBLOQUEADO:** 🌲 FLORESTA DOS VERBOS (Fase 2)"
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[1]
  },
  2: { // Floresta dos Verbos
    intro: {
      narrator: "Chrónos, o Guardião do Tempo",
      avatar: "⏰",
      text: (playerName) => `Bem-vindo à Floresta dos Verbos, ${playerName}. Eu sou Chrónos, o Guardião do Tempo. Esta floresta é perigosa - as árvores aqui mudam constantemente entre passado, presente e futuro. Muitos aventureiros se perderam aqui, presos em loops temporais porque não sabiam CONJUGAR VERBOS. Vou te ensinar os segredos do TEMPO e da CONJUGAÇÃO. Só assim você conseguirá atravessar esta floresta vivo, ${playerName}!`
    },
    chapters: [
      {
        id: 1,
        name: "Capítulo 1: Conjugação - O que é?",
        icon: "🔄",
        description: "Entenda o que significa conjugar um verbo",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Viajante Congelado no Tempo",
            content: "Na entrada da floresta, você encontra um homem paralisado, repetindo:\n\n'Correr... correr... correr...'\n\nChrónos explica: 'Ele só sabe o INFINITIVO! Não consegue conjugar o verbo para diferentes pessoas e tempos. Está preso! Vou te ensinar para você não ter o mesmo destino.'"
          },
          {
            type: "content",
            title: "📚 AULA 1: O que é CONJUGAR?",
            content: "**CONJUGAR** é ADAPTAR o verbo para diferentes PESSOAS e TEMPOS.\n\n**🎯 ANALOGIA SIMPLES:**\nÉ como ajustar um item de roupa para cada pessoa:\n- O verbo CORRER é uma roupa \"tamanho único\"\n- Conjugar é fazer ele servir em cada um:\n  - EU corro (serve em mim)\n  - TU corres (serve em você)\n  - ELE corre (serve nele)\n\n**INFINITIVO vs CONJUGADO:**\n\n**INFINITIVO** = forma original/dicionário\n- FALAR, COMER, PARTIR\n- Termina em -AR, -ER, -IR\n- Não indica quem nem quando\n\n**CONJUGADO** = adaptado para pessoa e tempo\n- EU FALO, TU FALAS, ELE FALA\n- Indica QUEM faz e QUANDO faz\n\n**EXEMPLO PRÁTICO:**\n❌ 'Eu correr ontem' (errado - infinitivo)\n✅ 'Eu CORRI ontem' (certo - conjugado no passado)\n\n❌ 'Eles comer agora' (errado)\n✅ 'Eles COMEM agora' (certo)"
          },
          {
            type: "content",
            title: "🎯 As 6 PESSOAS da Conjugação",
            content: "Todo verbo pode ser conjugado em 6 PESSOAS:\n\n**SINGULAR:**\n1. **EU** (1ª pessoa) - quem fala\n   - Eu falo, eu como, eu parto\n2. **TU / VOCÊ** (2ª pessoa) - com quem fala\n   - Tu falas, você fala\n3. **ELE / ELA** (3ª pessoa) - de quem fala\n   - Ele fala, ela fala\n\n**PLURAL:**\n4. **NÓS** (1ª pessoa plural)\n   - Nós falamos\n5. **VÓS / VOCÊS** (2ª pessoa plural)\n   - Vós falais, vocês falam\n6. **ELES / ELAS** (3ª pessoa plural)\n   - Eles falam, elas falam\n\n**🔮 TRUQUE PARA LEMBRAR:**\nCada pessoa tem sua TERMINAÇÃO específica:\n- EU = -O (falo, como, parto)\n- TU = -S (falas, comes, partes)\n- ELE = sem S (fala, come, parte)\n- NÓS = -MOS (falamos, comemos, partimos)\n- ELES = -M (falam, comem, partem)"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Qual está CONJUGADO (adaptado para pessoa)?",
            options: ["correr", "corro", "comer", "partir"],
            answer: 1,
            xp: 30,
            explanation: "✅ CORRETO! 'Corro' está conjugado para EU (1ª pessoa). 'Correr', 'comer', 'partir' estão no INFINITIVO (forma original)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Conjugue FALAR para EU:",
            options: ["falar", "falo", "fala", "falas"],
            answer: 1,
            xp: 35,
            explanation: "✅ PERFEITO! EU = FALO (terminação -O). 'Falar' é infinitivo, 'fala' é para ELE, 'falas' é para TU."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 3: Conjugue COMER para NÓS:",
            options: ["comemos", "comem", "come", "comer"],
            answer: 0,
            xp: 40,
            explanation: "✅ EXCELENTE! NÓS = COMEMOS (terminação -MOS). Sempre use -MOS para NÓS!"
          }
        ]
      },
      {
        id: 2,
        name: "Capítulo 2: Tempo PRESENTE",
        icon: "📅",
        description: "Aprenda a conjugar no tempo presente",
        pages: [
          {
            type: "narrative",
            title: "🎭 A Clareira do Agora",
            content: "Você chega a uma clareira onde tudo acontece AGORA:\n\nPássaros CANTAM, flores CRESCEM, rio FLUI.\n\nChrónos: 'Esta é a Clareira do Presente! Aqui você aprenderá o tempo mais importante: o PRESENTE - o que acontece AGORA.'"
          },
          {
            type: "content",
            title: "📚 AULA 2: Tempo PRESENTE",
            content: "**PRESENTE** expressa ações que acontecem AGORA, HABITUALMENTE ou VERDADES UNIVERSAIS.\n\n**🎯 QUANDO USAR:**\n\n**1. AGORA (neste momento):**\n- 'Eu ESTUDO agora'\n- 'Ele COME pizza'\n\n**2. HÁBITO/ROTINA:**\n- 'Eu TRABALHO todo dia'\n- 'Ela ACORDA cedo'\n\n**3. VERDADE UNIVERSAL:**\n- 'O sol NASCE no leste'\n- 'Água FERVE a 100°C'\n\n**CONJUGAÇÃO NO PRESENTE:**\n\n**Verbo FALAR (-AR):**\n- Eu falo\n- Tu falas / Você fala\n- Ele/Ela fala\n- Nós falamos\n- Vós falais / Vocês falam\n- Eles/Elas falam\n\n**Verbo COMER (-ER):**\n- Eu como\n- Tu comes / Você come\n- Ele/Ela come\n- Nós comemos\n- Vós comeis / Vocês comem\n- Eles/Elas comem\n\n**Verbo PARTIR (-IR):**\n- Eu parto\n- Tu partes / Você parte\n- Ele/Ela parte\n- Nós partimos\n- Vós partis / Vocês partem\n- Eles/Elas partem"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Complete: 'Eu ____ pizza todo dia' (comer - presente):",
            options: ["como", "comi", "comerei", "comer"],
            answer: 0,
            xp: 30,
            explanation: "✅ CORRETO! 'Como' é o presente de COMER para EU. Expressa hábito (todo dia)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Complete: 'Nós ____ português' (estudar - presente):",
            options: ["estudo", "estudamos", "estudam", "estudar"],
            answer: 1,
            xp: 35,
            explanation: "✅ PERFEITO! 'Estudamos' é o presente de ESTUDAR para NÓS (terminação -MOS)."
          }
        ]
      },
      {
        id: 3,
        name: "Capítulo 3: Tempo PASSADO",
        icon: "⏪",
        description: "Aprenda a falar sobre o que já aconteceu",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Vale das Memórias",
            content: "Você entra em um vale onde tudo já aconteceu:\n\nÁrvores CAÍRAM, batalhas ACONTECERAM, heróis PASSARAM.\n\nChrónos: 'Bem-vindo ao Vale das Memórias! Aqui tudo está no PASSADO. Você precisa dominar os tempos passados para atravessar!'"
          },
          {
            type: "content",
            title: "📚 AULA 3: Tempo PASSADO (Pretérito Perfeito)",
            content: "**PRETÉRITO PERFEITO** expressa ações que aconteceram e TERMINARAM no passado.\n\n**🎯 ANALOGIA SIMPLES:**\nÉ como uma missão concluída no jogo:\n- ✓ Eu DERROTEI o boss (acabou, missão completa)\n- ✓ Ela ESTUDOU ontem (acabou)\n\n**QUANDO USAR:**\n- Ações concluídas no passado\n- Palavras-chave: ontem, semana passada, já, foi\n\n**CONJUGAÇÃO NO PASSADO:**\n\n**Verbo FALAR (-AR):**\n- Eu falei\n- Tu falaste / Você falou\n- Ele/Ela falou\n- Nós falamos (igual ao presente!)\n- Vós falastes / Vocês falaram\n- Eles/Elas falaram\n\n**Verbo COMER (-ER):**\n- Eu comi\n- Tu comeste / Você comeu\n- Ele/Ela comeu\n- Nós comemos (igual ao presente!)\n- Vós comestes / Vocês comeram\n- Eles/Elas comeram\n\n**Verbo PARTIR (-IR):**\n- Eu parti\n- Tu partiste / Você partiu\n- Ele/Ela partiu\n- Nós partimos (igual ao presente!)\n- Vós partistes / Vocês partiram\n- Eles/Elas partiram\n\n**🔮 TRUQUE:**\nPassado para EU sempre termina em -I:\n- Eu falEI, comI, partI"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Complete: 'Eu ____ ontem' (comer - passado):",
            options: ["como", "comi", "comerei", "comer"],
            answer: 1,
            xp: 35,
            explanation: "✅ CORRETO! 'Comi' é o passado de COMER para EU. Termina em -I (característica do passado para EU)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Transforme para o passado: 'Ele fala muito'",
            options: ["Ele fala muito", "Ele falou muito", "Ele falará muito", "Ele falar muito"],
            answer: 1,
            xp: 40,
            explanation: "✅ PERFEITO! 'Falou' é o passado de FALAR para ELE. Ação terminada."
          }
        ]
      },
      {
        id: 4,
        name: "Capítulo 4: Tempo FUTURO",
        icon: "⏩",
        description: "Aprenda a falar sobre o que vai acontecer",
        pages: [
          {
            type: "narrative",
            title: "🎭 A Ponte do Amanhã",
            content: "À frente, uma ponte que leva ao futuro:\n\nFlores FLORESCERÃO, pássaros CANTARÃO, sol BRILHARÁ.\n\nChrónos: 'Esta ponte leva ao futuro! Para atravessá-la, você precisa dominar o FUTURO DO INDICATIVO!'"
          },
          {
            type: "content",
            title: "📚 AULA 4: Tempo FUTURO",
            content: "**FUTURO** expressa ações que AINDA VÃO ACONTECER.\n\n**🎯 ANALOGIA SIMPLES:**\nÉ como uma missão que você AINDA VAI fazer:\n- 📜 Eu ESTUDAREI amanhã (ainda não fiz)\n- 📜 Ela VIAJARÁ semana que vem\n\n**QUANDO USAR:**\n- Ações futuras\n- Palavras-chave: amanhã, semana que vem, depois, em breve\n\n**CONJUGAÇÃO NO FUTURO:**\n\n**Verbo FALAR:**\n- Eu falarei\n- Tu falarás / Você falará\n- Ele/Ela falará\n- Nós falaremos\n- Vós falareis / Vocês falarão\n- Eles/Elas falarão\n\n**Verbo COMER:**\n- Eu comerei\n- Tu comerás / Você comerá\n- Ele/Ela comerá\n- Nós comeremos\n- Vós comereis / Vocês comerão\n- Eles/Elas comerão\n\n**Verbo PARTIR:**\n- Eu partirei\n- Tu partirás / Você partirá\n- Ele/Ela partirá\n- Nós partiremos\n- Vós partireis / Vocês partirão\n- Eles/Elas partirão\n\n**🔮 TRUQUE:**\nFuturo = INFINITIVO + terminação\n- Falar + ei = FalarEI\n- Comer + ei = ComerEI\n- Partir + ei = PartirEI"
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Complete: 'Eu ____ amanhã' (estudar - futuro):",
            options: ["estudo", "estudei", "estudarei", "estudar"],
            answer: 2,
            xp: 35,
            explanation: "✅ CORRETO! 'Estudarei' é o futuro de ESTUDAR para EU. Ação que ainda vai acontecer (amanhã)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Qual tempo verbal em 'Ela viajará semana que vem'?",
            options: ["Presente", "Passado", "Futuro", "Infinitivo"],
            answer: 2,
            xp: 40,
            explanation: "✅ PERFEITO! 'Viajará' está no FUTURO - ação que ainda vai acontecer (semana que vem)."
          }
        ]
      },
      {
        id: 5,
        name: "Capítulo 5: Verbos Irregulares",
        icon: "⚡",
        description: "Domíne os verbos que não seguem as regras",
        pages: [
          {
            type: "narrative",
            title: "🎭 O Bosque Caótico",
            content: "Você chega a uma parte da floresta onde NADA segue as regras:\n\nChrónos alerta: 'Cuidado! Aqui moram os VERBOS IRREGULARES! Eles não seguem os padrões normais. São traiçoeiros! SER, TER, IR, VIR - todos eles têm formas especiais!'"
          },
          {
            type: "content",
            title: "📚 AULA 5: Verbos Irregulares - Os Rebeldes",
            content: "**VERBOS IRREGULARES** não seguem o padrão normal de conjugação.\n\n**🎯 ANALOGIA SIMPLES:**\nSão como chefes especiais em jogos - têm habilidades únicas!\n- Verbo regular: previsível, segue padrão\n- Verbo irregular: imprevisível, tem forma própria\n\n**PRINCIPAIS VERBOS IRREGULARES:**\n\n**SER (presente):**\n- Eu SOU (não é 'sero'!)\n- Tu és / Você é\n- Ele/Ela é\n- Nós somos\n- Vocês são\n- Eles/Elas são\n\n**TER (presente):**\n- Eu TENHO (não é 'teno'!)\n- Tu tens / Você tem\n- Ele/Ela tem\n- Nós temos\n- Vocês têm\n- Eles/Elas têm\n\n**IR (presente):**\n- Eu VOU (completamente diferente de 'ir'!)\n- Tu vais / Você vai\n- Ele/Ela vai\n- Nós vamos\n- Vocês vão\n- Eles/Elas vão\n\n**FAZER (presente):**\n- Eu faço\n- Tu fazes / Você faz\n- Ele/Ela faz\n- Nós fazemos\n- Vocês fazem\n- Eles/Elas fazem\n\n**⚡ IMPORTANTE:**\nIrregulares precisam ser MEMORIZADOS!\nNão têm padrão fixo."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 1: Complete: 'Eu ____ feliz' (ser - presente):",
            options: ["sero", "sou", "so", "ser"],
            answer: 1,
            xp: 40,
            explanation: "✅ CORRETO! 'Sou' é irregular - não segue padrão. EU SOU (não 'sero' como seria regular)."
          },
          {
            type: "practice",
            question: "🎯 PRÁTICA 2: Complete: 'Eu ____ ao mercado' (ir - presente):",
            options: ["iro", "vou", "vo", "ir"],
            answer: 1,
            xp: 45,
            explanation: "✅ PERFEITO! 'Vou' é super irregular - completamente diferente de 'ir'! EU VOU é a forma correta."
          },
          {
            type: "narrative",
            title: "🎉 FASE 2 COMPLETA - Mestre do Tempo!",
            content: "Chrónos está impressionado!\n\n'MAGNÍFICO! Você dominou os TEMPOS VERBAIS e a CONJUGAÇÃO! Conseguiu atravessar a Floresta dos Verbos - algo que poucos conseguem!'\n\nVocê ganhou o título: **SENHOR DO TEMPO VERBAL**\n\nChrónos: 'Agora você deve ir à MONTANHA DAS FRASES! Lá, o Arquiteto Sintático te ensinará como ESTRUTURAR FRASES COMPLETAS! Sua jornada continua!'\n\n**DESBLOQUEADO:** ⛰️ MONTANHA DAS FRASES (Fase 3)"
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[2]
  },
  3: { // Montanha das Frases
    intro: {
      narrator: "Arquiteto Sintático",
      avatar: "🏗️",
      text: (playerName) => `Bem-vindo à Montanha das Frases, ${playerName}! Aqui você aprenderá como as palavras se organizam para formar frases completas. Prepare-se para se tornar um verdadeiro arquiteto da linguagem!`
    },
    chapters: [
      {
        id: 1,
        name: "Sujeito e Predicado",
        icon: "🎯",
        pages: [
          {
            type: "content",
            title: "Estrutura da Frase",
            content: "Toda frase tem duas partes principais: SUJEITO (quem faz) e PREDICADO (o que faz). Exemplo: 'O menino (sujeito) brinca no parque (predicado)'."
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[3]
  },
  4: { // Deserto da Concordância
    intro: {
      narrator: "Guardião da Harmonia",
      avatar: "⚖️",
      text: (playerName) => `O Deserto da Concordância é árido e traiçoeiro, ${playerName}! Aqui você aprenderá a fazer as palavras concordarem entre si. A harmonia é essencial para um verdadeiro mestre da gramática!`
    },
    chapters: [
      {
        id: 1,
        name: "Concordância Nominal",
        icon: "📜",
        pages: [
          {
            type: "content",
            title: "Concordância de Gênero e Número",
            content: "Os artigos, adjetivos e numerais devem concordar com o substantivo. Exemplo: As meninas bonitas (tudo no feminino plural)."
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[4]
  },
  5: { // Caverna da Regência
    intro: {
      narrator: "Oráculo da Preposição",
      avatar: "🔮",
      text: (playerName) => `A Caverna da Regência esconde os segredos das preposições e da temida crase, ${playerName}! Poucos aventureiros dominam este conhecimento. Você será um deles?`
    },
    chapters: [
      {
        id: 1,
        name: "Regência Verbal",
        icon: "🎓",
        pages: [
          {
            type: "content",
            title: "O que é Regência?",
            content: "Regência é a relação entre um verbo (ou nome) e seus complementos. Alguns verbos pedem preposições específicas."
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[5]
  },
  6: { // Castelo da Pontuação
    intro: {
      narrator: "Mestre dos Sinais",
      avatar: "✍️",
      text: (playerName) => `No Castelo da Pontuação, você aprenderá a dar ritmo e clareza aos seus textos, ${playerName}! Os sinais de pontuação são a música da escrita.`
    },
    chapters: [
      {
        id: 1,
        name: "Vírgulas e Pontos",
        icon: "📍",
        pages: [
          {
            type: "content",
            title: "Uso da Vírgula",
            content: "A vírgula marca pausas e separa elementos. Nunca separe sujeito de verbo com vírgula!"
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[6]
  },
  7: { // Torre do Mestre
    intro: {
      narrator: "Grande Mestre",
      avatar: "👑",
      text: (playerName) => `Parabéns, ${playerName}! Você chegou à Torre do Mestre! Aqui você enfrentará desafios que misturam tudo o que aprendeu. Prove que você é digno do título de Gramática Imortal!`
    },
    chapters: [
      {
        id: 1,
        name: "Revisão Geral",
        icon: "🌟",
        pages: [
          {
            type: "content",
            title: "Domínio Total",
            content: "Agora você revisará todos os conceitos aprendidos na sua jornada. Prepare-se para o desafio final!"
          }
        ]
      }
    ],
    battleQuestions: QUESTIONS[7]
  }
};

// Sound effects (using Web Audio API)
const playSound = (type) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    switch(type) {
      case 'correct':
        oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
        break;
      case 'wrong':
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(150, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
        break;
      case 'levelup':
        oscillator.frequency.setValueAtTime(392, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime + 0.15);
        oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.3);
        oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.45);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.6);
        break;
      case 'click':
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.05);
        break;
      case 'page':
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
        break;
      default:
        // No sound for unknown types
        break;
    }
  } catch (e) {
    // Audio not supported
  }
};

// ==================== COMPONENTS ====================

// Narrative Story Renderer - Para páginas de história/narrativa
const NarrativeContent = ({ children }) => {
  const parseNarrative = (text) => {
    if (!text) return [];

    const lines = text.split('\n');
    const elements = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Empty lines
      if (line === '') {
        elements.push(<div key={key++} className="h-4" />);
        continue;
      }

      // Detectar diálogos com aspas simples ou duplas
      const dialogMatch = line.match(/^['"](.+?)['"](\s*-\s*(.+))?$/);
      if (dialogMatch) {
        const dialog = dialogMatch[1];
        const speaker = dialogMatch[3];

        elements.push(
          <div key={key++} className="mb-4 ml-6 relative">
            <div className="absolute -left-6 top-0 text-2xl text-purple-400">💬</div>
            <div className="bg-purple-900/30 border-l-4 border-purple-400/60 rounded-r-xl p-4 backdrop-blur-sm">
              <p className="text-white/95 text-lg italic leading-relaxed mb-1">
                "{dialog}"
              </p>
              {speaker && (
                <p className="text-purple-300 text-sm font-semibold mt-2">
                  — {speaker}
                </p>
              )}
            </div>
          </div>
        );
        continue;
      }

      // Detectar conquista de título épica
      const titleMatch = line.match(/Você ganhou o título:\s*\*\*(.*?)\*\*/);
      if (titleMatch) {
        const titleName = titleMatch[1];
        elements.push(
          <div key={key++} className="my-8">
            <div className="relative">
              {/* Brilho de fundo */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 via-yellow-500/30 to-amber-500/20 blur-2xl"></div>

              {/* Card da conquista */}
              <div className="relative bg-gradient-to-r from-amber-900/80 via-yellow-800/80 to-amber-900/80 rounded-2xl p-6 border-4 border-amber-400/70 shadow-2xl shadow-amber-500/40">
                <div className="text-center space-y-3">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-3xl">⚔️</span>
                    <p className="text-amber-200 font-semibold text-lg tracking-wider uppercase">
                      Título Conquistado
                    </p>
                    <span className="text-3xl">⚔️</span>
                  </div>

                  <div className="relative inline-block">
                    <div className="absolute inset-0 bg-yellow-300 blur-md opacity-40"></div>
                    <h3 className="relative text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-amber-100 to-yellow-200 drop-shadow-lg px-6 py-2">
                      {titleName}
                    </h3>
                  </div>

                  <div className="flex items-center justify-center gap-3 mt-4">
                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-400"></div>
                    <span className="text-2xl">✨</span>
                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-400"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        continue;
      }

      // Detectar desbloqueio épico
      const unlockMatch = line.match(/\*\*DESBLOQUEADO:\*\*\s*(.+)/);
      if (unlockMatch) {
        const unlockText = unlockMatch[1];
        elements.push(
          <div key={key++} className="my-6">
            <div className="bg-gradient-to-r from-green-900/60 via-emerald-800/60 to-green-900/60 rounded-xl p-5 border-2 border-green-400/60 shadow-xl shadow-green-500/30">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500/30 border-2 border-green-300 flex items-center justify-center">
                  <span className="text-2xl">🔓</span>
                </div>
                <div className="flex-1">
                  <p className="text-green-200 font-semibold text-sm uppercase tracking-wider mb-1">
                    Desbloqueado
                  </p>
                  <p className="text-white text-xl font-bold">
                    {unlockText}
                  </p>
                </div>
                <span className="text-3xl">✨</span>
              </div>
            </div>
          </div>
        );
        continue;
      }

      // Narração do mestre/narrador
      if (line.includes('Mestre') || line.includes('sussurra') || line.includes('diz') || line.includes('Chrónos') || line.includes('Guardião')) {
        elements.push(
          <div key={key++} className="mb-4 bg-amber-900/20 border border-amber-500/30 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🧙‍♂️</span>
              <p className="text-amber-100 text-lg leading-relaxed italic">
                {line}
              </p>
            </div>
          </div>
        );
        continue;
      }

      // Texto narrativo normal
      elements.push(
        <p key={key++} className="text-white/90 text-lg leading-relaxed mb-4 indent-4">
          {line}
        </p>
      );
    }

    return elements;
  };

  return (
    <div className="relative">
      {/* Livro de histórias aberto */}
      <div className="relative bg-gradient-to-br from-slate-800/90 via-indigo-900/70 to-slate-900/90 rounded-2xl p-8 shadow-2xl border-2 border-indigo-500/30 backdrop-blur-sm">
        {/* Decorações de canto - tema de livro mágico */}
        <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-indigo-400/40 rounded-tl-2xl"></div>
        <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-indigo-400/40 rounded-tr-2xl"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-indigo-400/40 rounded-bl-2xl"></div>
        <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-indigo-400/40 rounded-br-2xl"></div>

        {/* Linha decorativa superior */}
        <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent"></div>

        {/* Conteúdo */}
        <div className="relative z-10">
          {parseNarrative(children)}
        </div>

        {/* Ícone de livro no canto */}
        <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-gradient-to-br from-indigo-600/80 to-purple-600/80 rounded-full border-2 border-indigo-400/50 shadow-lg flex items-center justify-center backdrop-blur-sm">
          <span className="text-xl">📖</span>
        </div>
      </div>
    </div>
  );
};

// Scroll/Parchment Content Renderer - Converts markdown-like text to styled HTML
const ScrollContent = ({ children }) => {
  const parseContent = (text) => {
    if (!text) return [];

    const lines = text.split('\n');
    const elements = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Empty lines
      if (line.trim() === '') {
        elements.push(<div key={key++} className="h-3" />);
        continue;
      }

      // Section headers with emoji (🎯, ✨, etc)
      if (line.match(/^[🎯✨📚🔥🔮⚡🌟]\s*\*\*.*\*\*:?$/)) {
        const emoji = line.match(/^([🎯✨📚🔥🔮⚡🌟])/)[1];
        const text = line.replace(/^[🎯✨📚🔥🔮⚡🌟]\s*\*\*(.*)\*\*:?$/, '$1');
        elements.push(
          <div key={key++} className="flex items-center gap-3 mt-6 mb-4 pb-3 border-b border-amber-500/30">
            <span className="text-4xl">{emoji}</span>
            <h3 className="text-2xl font-bold text-amber-300 tracking-wide">{text}</h3>
          </div>
        );
        continue;
      }

      // Bold headers (**TEXT**)
      if (line.match(/^\*\*.*\*\*:?$/)) {
        const text = line.replace(/^\*\*(.*)\*\*:?$/, '$1');
        elements.push(
          <h4 key={key++} className="text-xl font-bold text-amber-200 mt-4 mb-3">
            {text}
          </h4>
        );
        continue;
      }

      // Bullet points with emoji
      if (line.match(/^[-•]\s/)) {
        const text = line.replace(/^[-•]\s/, '');
        const processed = processBoldInline(text);
        elements.push(
          <div key={key++} className="flex gap-3 mb-3 ml-4">
            <span className="text-amber-400 mt-1.5 text-lg">✦</span>
            <p className="text-white/90 leading-relaxed flex-1 text-lg">{processed}</p>
          </div>
        );
        continue;
      }

      // Check marks (✓)
      if (line.includes('✓')) {
        const processed = processBoldInline(line);
        elements.push(
          <div key={key++} className="flex gap-2 mb-3 ml-6 text-green-300 text-lg">
            {processed}
          </div>
        );
        continue;
      }

      // Regular paragraphs (may contain inline **bold**)
      const processed = processBoldInline(line);
      elements.push(
        <p key={key++} className="text-white/90 leading-relaxed mb-4 text-lg">
          {processed}
        </p>
      );
    }

    return elements;
  };

  // Process inline bold (**text**)
  const processBoldInline = (text) => {
    const parts = [];
    let lastIndex = 0;
    let key = 0;
    const regex = /\*\*(.*?)\*\*/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add bold text
      parts.push(
        <strong key={key++} className="text-amber-300 font-bold">
          {match[1]}
        </strong>
      );
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <div className="relative">
      {/* Pergaminho com tema dark harmonizado */}
      <div className="relative bg-gradient-to-br from-slate-800/95 via-purple-900/80 to-slate-900/95 rounded-2xl p-8 shadow-2xl border-2 border-amber-500/30 backdrop-blur-sm">
        {/* Decorações de canto - sutis e douradas */}
        <div className="absolute top-0 left-0 w-12 h-12 border-l-2 border-t-2 border-amber-400/40 rounded-tl-2xl"></div>
        <div className="absolute top-0 right-0 w-12 h-12 border-r-2 border-t-2 border-amber-400/40 rounded-tr-2xl"></div>
        <div className="absolute bottom-0 left-0 w-12 h-12 border-l-2 border-b-2 border-amber-400/40 rounded-bl-2xl"></div>
        <div className="absolute bottom-0 right-0 w-12 h-12 border-r-2 border-b-2 border-amber-400/40 rounded-br-2xl"></div>

        {/* Brilho sutil no topo */}
        <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"></div>

        {/* Conteúdo */}
        <div className="relative z-10">
          {parseContent(children)}
        </div>

        {/* Pequeno ícone decorativo no canto */}
        <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-gradient-to-br from-amber-600/80 to-yellow-600/80 rounded-full border-2 border-amber-400/50 shadow-lg flex items-center justify-center backdrop-blur-sm">
          <span className="text-xl">✨</span>
        </div>
      </div>
    </div>
  );
};

// Animated background particles
const ParticleBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-20"
          style={{
            width: Math.random() * 10 + 5 + 'px',
            height: Math.random() * 10 + 5 + 'px',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            background: `hsl(${Math.random() * 60 + 30}, 80%, 60%)`,
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
};

// Phase Card Component
const PhaseCard = ({ phase, isUnlocked, isCompleted, lessonsCompleted, totalLessons, onClick }) => {
  return (
    <button
      onClick={onClick}
      disabled={!isUnlocked}
      className={`w-full p-6 rounded-2xl border-2 transition-all duration-300 text-left ${
        !isUnlocked
          ? 'bg-slate-800/30 border-slate-700/30 opacity-50 cursor-not-allowed'
          : 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-600/50 hover:border-amber-400/50 hover:scale-[1.02] cursor-pointer'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="text-5xl">{phase.icon}</div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{phase.name}</h3>
            <p className="text-sm text-slate-400 mb-2">{phase.subtitle}</p>
            {isUnlocked && (
              <div className="flex items-center gap-2">
                <div className="h-2 bg-slate-700 rounded-full flex-1 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-yellow-400 transition-all duration-500"
                    style={{ width: `${totalLessons > 0 ? (lessonsCompleted / totalLessons) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400">{lessonsCompleted}/{totalLessons}</span>
              </div>
            )}
          </div>
        </div>
        {isCompleted && <div className="text-3xl">✅</div>}
        {!isUnlocked && <div className="text-2xl">🔒</div>}
      </div>
      {!isUnlocked && (
        <div className="mt-3 text-sm text-amber-400">
          🔒 Desbloqueado no nível {phase.unlockLevel}
        </div>
      )}
    </button>
  );
};

// Narrator Dialog Component
const NarratorDialog = ({ narrator, avatar, children }) => {
  return (
    <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-2xl p-6 border border-indigo-500/30 backdrop-blur-sm">
      <div className="flex items-start gap-4">
        <div className="text-5xl">{avatar}</div>
        <div className="flex-1">
          <div className="text-indigo-300 font-bold mb-2">{narrator}</div>
          <p className="text-white/90 leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  );
};

// Chapter Card Component
const ChapterCard = ({ chapter, isCompleted, isLocked, onClick }) => {
  return (
    <button
      onClick={onClick}
      disabled={isLocked}
      className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
        isLocked
          ? 'bg-slate-800/30 border-slate-700/30 opacity-50 cursor-not-allowed'
          : isCompleted
          ? 'bg-gradient-to-r from-green-900/40 to-emerald-900/40 border-green-500/30 hover:border-green-400/50 hover:scale-[1.02]'
          : 'bg-slate-800/50 border-slate-600/50 hover:border-amber-400/50 hover:scale-[1.02]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{chapter.icon}</span>
          <div>
            <h4 className="font-bold text-white">{chapter.name}</h4>
            {chapter.pages && (
              <p className="text-xs text-slate-400">{chapter.pages.length} páginas</p>
            )}
          </div>
        </div>
        {isCompleted && <span className="text-2xl">✓</span>}
        {isLocked && <span className="text-xl">🔒</span>}
      </div>
    </button>
  );
};

// Lesson Page Component
const LessonPage = ({ page, onNext, onPracticeAnswer, practiceState }) => {
  // Páginas narrativas (história do RPG)
  if (page.type === 'narrative') {
    return (
      <div className="space-y-6">
        {/* Título da cena */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="text-5xl">🎭</span>
            <h2 className="text-3xl font-bold text-purple-300">{page.title}</h2>
          </div>
        </div>

        {/* Livro de histórias com conteúdo narrativo */}
        <NarrativeContent>{page.content}</NarrativeContent>

        <button
          onClick={onNext}
          className="w-full py-4 px-6 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-bold rounded-xl text-lg transition-all duration-300 hover:scale-[1.02] shadow-lg"
        >
          Continuar a História →
        </button>
      </div>
    );
  }

  // Páginas de conteúdo educacional
  if (page.type === 'content') {
    return (
      <div className="space-y-6">
        {/* Título da página */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="text-5xl">🎓</span>
            <h2 className="text-3xl font-bold text-amber-300">{page.title}</h2>
          </div>
        </div>

        {/* Pergaminho com conteúdo */}
        <ScrollContent>{page.content}</ScrollContent>

        <button
          onClick={onNext}
          className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl text-lg transition-all duration-300 hover:scale-[1.02] shadow-lg"
        >
          Entendi! Próximo →
        </button>
      </div>
    );
  }

  // Páginas de prática
  if (page.type === 'practice') {
    return (
      <div className="space-y-6">
        {/* Header da Arena de Desafios */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-red-900/60 via-orange-900/60 to-red-900/60 rounded-full border-2 border-orange-500/50 shadow-lg">
            <span className="text-3xl">⚔️</span>
            <span className="text-orange-300 font-bold text-xl uppercase tracking-wider">Desafio de Combate</span>
            <span className="text-3xl">🛡️</span>
          </div>
        </div>

        {/* Arena de Desafio */}
        <div className="relative bg-gradient-to-br from-slate-800/95 via-red-950/80 to-slate-900/95 rounded-3xl p-8 border-2 border-red-500/40 backdrop-blur-sm shadow-2xl">
          {/* Decorações de canto - tema de arena */}
          <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-orange-400/40 rounded-tl-3xl"></div>
          <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-orange-400/40 rounded-tr-3xl"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-orange-400/40 rounded-bl-3xl"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-orange-400/40 rounded-br-3xl"></div>

          {/* Brilho superior */}
          <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>

          {/* Pergunta do Desafio */}
          <div className="relative z-10 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center border-2 border-orange-300/50 shadow-lg">
                <span className="text-2xl">❓</span>
              </div>
              <h3 className="text-2xl text-white font-bold leading-tight pt-2">{page.question}</h3>
            </div>
          </div>

          {/* Opções de Ataque */}
          <div className="relative z-10 grid gap-4">
            {page.options.map((option, index) => {
              const isCorrect = index === page.answer;
              const isSelected = practiceState?.selected === index;
              const isAnswered = practiceState?.answered;

              let buttonClass = "group relative w-full p-5 rounded-xl text-left transition-all duration-300 border-2 font-medium text-lg ";

              if (isAnswered) {
                if (isCorrect) {
                  buttonClass += "bg-gradient-to-r from-green-900/40 to-emerald-900/40 border-green-400 text-green-100 shadow-lg shadow-green-500/20";
                } else if (isSelected && !isCorrect) {
                  buttonClass += "bg-gradient-to-r from-red-900/40 to-pink-900/40 border-red-400 text-red-100 shadow-lg shadow-red-500/20";
                } else {
                  buttonClass += "bg-slate-800/40 border-slate-700/40 text-slate-500";
                }
              } else {
                buttonClass += "bg-gradient-to-r from-slate-700/60 to-slate-800/60 border-slate-500/60 text-white hover:from-orange-800/50 hover:to-red-800/50 hover:border-orange-400/70 hover:scale-[1.03] hover:shadow-lg hover:shadow-orange-500/20 active:scale-[0.98]";
              }

              return (
                <button
                  key={index}
                  onClick={() => !isAnswered && onPracticeAnswer(index, isCorrect)}
                  disabled={isAnswered}
                  className={buttonClass}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold border-2 transition-all ${
                      isAnswered
                        ? isCorrect
                          ? 'bg-green-500/30 border-green-400 text-green-200'
                          : isSelected
                          ? 'bg-red-500/30 border-red-400 text-red-200'
                          : 'bg-slate-700/30 border-slate-600 text-slate-400'
                        : 'bg-orange-900/40 border-orange-600/60 text-orange-200 group-hover:bg-orange-700/60 group-hover:border-orange-400'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </div>
                    <span className="flex-1">{option}</span>
                    {isAnswered && isCorrect && <span className="text-3xl animate-bounce">⚔️</span>}
                    {isAnswered && isSelected && !isCorrect && <span className="text-3xl">💔</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Resultado do Combate */}
          {practiceState?.answered && page.explanation && (
            <div className={`relative z-10 mt-8 p-6 rounded-xl border-2 backdrop-blur-sm shadow-xl ${
              practiceState.correct
                ? 'bg-gradient-to-r from-green-900/40 to-emerald-900/40 border-green-400/60'
                : 'bg-gradient-to-r from-amber-900/40 to-yellow-900/40 border-amber-400/60'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center border-2 ${
                  practiceState.correct
                    ? 'bg-green-500/30 border-green-300'
                    : 'bg-amber-500/30 border-amber-300'
                }`}>
                  <span className="text-3xl">{practiceState.correct ? '🏆' : '📚'}</span>
                </div>
                <div className="flex-1">
                  <div className={`font-bold text-xl mb-3 ${
                    practiceState.correct ? 'text-green-200' : 'text-amber-200'
                  }`}>
                    {practiceState.correct ? '⚔️ Vitória! Golpe Certeiro!' : '🛡️ Defesa! Continue Treinando'}
                  </div>
                  <p className="text-white/90 leading-relaxed whitespace-pre-line">{page.explanation}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {practiceState?.answered && (
          <button
            onClick={onNext}
            className="w-full py-5 px-8 bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500 hover:from-orange-500 hover:via-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl text-xl transition-all duration-300 hover:scale-[1.03] shadow-xl shadow-orange-500/30 border-2 border-orange-300/50"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">⚔️</span>
              <span>Avançar na Jornada</span>
              <span className="text-2xl">✨</span>
            </div>
          </button>
        )}
      </div>
    );
  }

  return null;
};

// XP Bar Component
const XPBar = ({ currentXP, level, title }) => {
  const currentLevelData = TITLES[level - 1] || TITLES[0];
  const nextLevelData = TITLES[level] || TITLES[TITLES.length - 1];
  const xpInLevel = currentXP - currentLevelData.xp;
  const xpNeeded = nextLevelData.xp - currentLevelData.xp;
  const progress = Math.min((xpInLevel / xpNeeded) * 100, 100);

  return (
    <div className="bg-gradient-to-r from-amber-900/40 to-yellow-900/40 rounded-2xl p-4 border border-amber-500/30 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{currentLevelData.emoji}</span>
          <div>
            <div className="text-amber-200 font-bold text-sm">Nível {level}</div>
            <div className="text-amber-100 text-xs">{title}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-amber-300 font-bold">{currentXP.toLocaleString()} XP</div>
          <div className="text-amber-200/60 text-xs">Próximo: {nextLevelData.xp.toLocaleString()}</div>
        </div>
      </div>
      <div className="h-3 bg-black/30 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-300 rounded-full transition-all duration-1000 ease-out relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/30" />
        </div>
      </div>
    </div>
  );
};

// Health Hearts Component
const HealthHearts = ({ lives, maxLives = 3 }) => {
  return (
    <div className="flex gap-1">
      {[...Array(maxLives)].map((_, i) => (
        <span 
          key={i} 
          className={`text-2xl transition-all duration-300 ${i < lives ? 'scale-100' : 'scale-75 grayscale opacity-30'}`}
          style={{ 
            filter: i < lives ? 'drop-shadow(0 0 8px #ef4444)' : 'none',
            animation: i < lives ? 'heartbeat 1s ease-in-out infinite' : 'none',
            animationDelay: `${i * 0.1}s`
          }}
        >
          ❤️
        </span>
      ))}
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};

// Combo Display Component
const ComboDisplay = ({ count, hasShield }) => {
  const isCrit = count >= 5;
  return (
    <div className="flex items-center justify-center gap-2 animate-comboIn">
      {isCrit ? (
        <div className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full border-2 border-yellow-300 shadow-lg shadow-yellow-500/50">
          <span className="text-white font-black text-lg tracking-widest">⚡ CRÍTICO!</span>
        </div>
      ) : (
        <div className={`px-4 py-2 rounded-full border-2 shadow-lg flex items-center gap-2 ${hasShield ? 'bg-gradient-to-r from-blue-600 to-cyan-500 border-cyan-300 shadow-cyan-500/50' : 'bg-gradient-to-r from-orange-600 to-red-500 border-orange-300 shadow-orange-500/50'}`}>
          <span className="text-white font-black text-lg">🔥 x{count}</span>
          {hasShield && <span className="text-white font-bold text-sm">+ 🛡️</span>}
        </div>
      )}
      <style>{`
        @keyframes comboIn {
          0% { opacity: 0; transform: scale(0.5) translateY(-10px); }
          60% { transform: scale(1.2) translateY(0); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-comboIn { animation: comboIn 0.4s ease-out; }
      `}</style>
    </div>
  );
};

// Damage Float Component
const DamageFloat = ({ value, isCrit, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 900);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 z-20 animate-floatUp">
      <span className={`font-black drop-shadow-lg ${isCrit ? 'text-yellow-300 text-4xl' : 'text-red-400 text-2xl'}`}>
        {isCrit ? `⚡ -${value}` : `-${value}`}
      </span>
      <style>{`
        @keyframes floatUp {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-70px); }
        }
        .animate-floatUp { animation: floatUp 0.9s ease-out forwards; }
      `}</style>
    </div>
  );
};

// Battle Report Component
const BattleReport = ({ wrongTopics }) => {
  if (wrongTopics.length === 0) {
    return (
      <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
        <div className="text-2xl mb-1">🏆</div>
        <p className="text-green-300 font-bold text-sm">Perfeito! Você não errou nenhuma questão!</p>
      </div>
    );
  }
  return (
    <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-left">
      <p className="text-blue-200 font-bold text-sm mb-2">✨ Você foi incrível! Para as próximas batalhas, recomendo treinar:</p>
      <ul className="space-y-1 mb-3">
        {wrongTopics.map((topic, i) => (
          <li key={i} className="text-white text-sm flex items-center gap-2">
            <span>📖</span><span>{topic}</span>
          </li>
        ))}
      </ul>
      <p className="text-slate-400 text-xs italic">Continue praticando — cada erro é um passo rumo à maestria! ⚔️</p>
    </div>
  );
};

// Enemy Component
const Enemy = ({ enemy, currentHP, maxHP, isHit, isDefeated }) => {
  const hpPercent = (currentHP / maxHP) * 100;
  const displayEmoji = isDefeated ? enemy.emoji
    : hpPercent > 66 ? enemy.emoji
    : hpPercent > 33 ? (enemy.damagedEmoji || enemy.emoji)
    : (enemy.desperateEmoji || enemy.emoji);

  return (
    <div className={`text-center transition-all duration-300 ${isHit ? 'animate-shake' : ''} ${isDefeated ? 'animate-defeat' : ''}`}>
      <div
        className="text-8xl mb-4 inline-block"
        style={{
          filter: isDefeated ? 'grayscale(1)' : `drop-shadow(0 0 20px ${hpPercent > 50 ? '#22c55e' : hpPercent > 25 ? '#f59e0b' : '#ef4444'})`,
          transform: isHit ? 'translateX(-10px)' : 'translateX(0)',
          transition: 'all 0.2s'
        }}
      >
        {displayEmoji}
      </div>
      <div className="text-xl font-bold text-white mb-2">{enemy.name}</div>
      <div className="w-48 mx-auto">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-red-300">HP</span>
          <span className="text-white">{currentHP}/{maxHP}</span>
        </div>
        <div className="h-4 bg-black/50 rounded-full overflow-hidden border border-red-500/30">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              hpPercent > 50 ? 'bg-gradient-to-r from-green-500 to-green-400' :
              hpPercent > 25 ? 'bg-gradient-to-r from-yellow-500 to-orange-400' :
              'bg-gradient-to-r from-red-600 to-red-400'
            }`}
            style={{ width: `${hpPercent}%` }}
          />
        </div>
      </div>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        @keyframes defeat {
          0% { opacity: 1; transform: scale(1) rotate(0deg); }
          100% { opacity: 0; transform: scale(0) rotate(360deg); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out; }
        .animate-defeat { animation: defeat 0.8s ease-out forwards; }
      `}</style>
    </div>
  );
};

// Battle Question Component
const BattleQuestion = ({ question, onAnswer, selectedAnswer, isAnswered, showHint, onShowHint }) => {
  return (
    <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-3xl p-6 border border-slate-600/30 backdrop-blur-sm shadow-2xl">
      <div className="flex justify-between items-center mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
          question.difficulty === 'basic' ? 'bg-green-500/20 text-green-300' :
          question.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-300' :
          'bg-red-500/20 text-red-300'
        }`}>
          {question.difficulty === 'basic' ? '⭐ Básico' :
           question.difficulty === 'intermediate' ? '⭐⭐ Intermediário' :
           '⭐⭐⭐ Avançado'}
        </span>
        <span className="text-amber-400">+{question.xp} XP</span>
      </div>

      <h3 className="text-xl text-white font-medium mb-6 leading-relaxed">
        {question.q}
      </h3>

      {!isAnswered && question.hint && (
        <button
          onClick={onShowHint}
          className="mb-4 text-sm text-amber-400 hover:text-amber-300 flex items-center gap-2"
        >
          🔮 {showHint ? question.hint : 'Precisa de uma dica?'}
        </button>
      )}

      <div className="grid gap-3">
        {question.options.map((option, index) => {
          const isCorrect = index === question.answer;
          const isSelected = selectedAnswer === index;
          
          let buttonClass = "w-full p-4 rounded-xl text-left transition-all duration-300 border-2 ";
          
          if (isAnswered) {
            if (isCorrect) {
              buttonClass += "bg-green-500/20 border-green-400 text-green-100";
            } else if (isSelected && !isCorrect) {
              buttonClass += "bg-red-500/20 border-red-400 text-red-100";
            } else {
              buttonClass += "bg-slate-700/30 border-slate-600/30 text-slate-400 opacity-50";
            }
          } else {
            buttonClass += "bg-slate-700/50 border-slate-600/50 text-white hover:bg-slate-600/50 hover:border-amber-400/50 hover:scale-[1.02] active:scale-[0.98]";
          }

          return (
            <button
              key={index}
              onClick={() => !isAnswered && onAnswer(index)}
              disabled={isAnswered}
              className={buttonClass}
            >
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                  isAnswered && isCorrect ? 'bg-green-500 text-white' :
                  isAnswered && isSelected && !isCorrect ? 'bg-red-500 text-white' :
                  'bg-slate-600 text-slate-300'
                }`}>
                  {String.fromCharCode(65 + index)}
                </span>
                <span className="flex-1">{option}</span>
                {isAnswered && isCorrect && <span className="text-2xl">✓</span>}
                {isAnswered && isSelected && !isCorrect && <span className="text-2xl">✗</span>}
              </div>
            </button>
          );
        })}
      </div>
      
      {isAnswered && question.hint && (
        <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="text-amber-300 text-sm">🔮 {question.hint}</div>
        </div>
      )}
    </div>
  );
};

// Stats Dashboard
const StatsDashboard = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/20">
        <div className="text-3xl mb-2">📜</div>
        <div className="text-2xl font-bold text-white">{stats.questionsAnswered}</div>
        <div className="text-sm text-blue-300">Questões</div>
      </div>
      <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/20">
        <div className="text-3xl mb-2">✅</div>
        <div className="text-2xl font-bold text-white">{stats.accuracy}%</div>
        <div className="text-sm text-green-300">Acurácia</div>
      </div>
      <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/20">
        <div className="text-3xl mb-2">📚</div>
        <div className="text-2xl font-bold text-white">{stats.lessonsCompleted}</div>
        <div className="text-sm text-purple-300">Lições</div>
      </div>
      <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl p-4 border border-orange-500/20">
        <div className="text-3xl mb-2">👹</div>
        <div className="text-2xl font-bold text-white">{stats.enemiesDefeated}</div>
        <div className="text-sm text-orange-300">Inimigos</div>
      </div>
    </div>
  );
};

// Epic Level Up / New Title Modal
const LevelUpModal = ({ show, newLevel, newTitle, onClose }) => {
  if (!show) return null;

  const titleData = TITLES[newLevel - 1];

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-hidden">
      {/* Partículas de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-amber-400 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Modal Principal */}
      <div className="relative max-w-2xl w-full">
        {/* Banner Superior */}
        <div className="text-center mb-6 animate-slideDown">
          <div className="inline-block relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-yellow-300 to-amber-500 opacity-20 blur-3xl"></div>
            <div className="relative px-12 py-4 bg-gradient-to-r from-amber-900 via-yellow-700 to-amber-900 border-4 border-amber-400 rounded-xl shadow-2xl">
              <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-amber-100 to-yellow-200 tracking-wider uppercase">
                ⚔️ ASCENSÃO! ⚔️
              </h2>
            </div>
          </div>
        </div>

        {/* Card Principal */}
        <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 rounded-3xl p-12 border-4 border-amber-500 shadow-2xl shadow-amber-500/50 animate-scaleIn relative overflow-hidden">
          {/* Brilho de fundo */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-purple-500/10 pointer-events-none"></div>

          <div className="relative z-10 text-center space-y-6">
            {/* Emoji/Ícone do Título */}
            <div className="text-9xl mb-4 animate-bounce-slow drop-shadow-2xl">
              {titleData.emoji}
            </div>

            {/* Nível */}
            <div>
              <div className="text-amber-400 text-lg font-semibold tracking-widest uppercase mb-2">
                Nível {newLevel}
              </div>

              {/* Título Principal */}
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-400 blur-xl opacity-50"></div>
                <h3 className="relative text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-200 drop-shadow-lg px-8 py-4">
                  {newTitle}
                </h3>
              </div>
            </div>

            {/* Linha decorativa */}
            <div className="flex items-center justify-center gap-4 my-8">
              <div className="h-px w-24 bg-gradient-to-r from-transparent to-amber-500"></div>
              <span className="text-3xl">✨</span>
              <div className="h-px w-24 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>

            {/* Mensagem */}
            <p className="text-xl text-amber-100 italic max-w-md mx-auto">
              Você transcendeu seus limites e alcançou um novo patamar de poder gramatical!
            </p>

            {/* Botão */}
            <button
              onClick={onClose}
              className="mt-8 px-12 py-5 bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 hover:from-amber-500 hover:via-yellow-400 hover:to-amber-500 text-black text-xl font-bold rounded-xl transition-all duration-300 hover:scale-110 shadow-2xl shadow-amber-500/50 border-2 border-yellow-300"
            >
              <div className="flex items-center gap-3">
                <span>⚔️</span>
                <span>CONTINUAR A SAGA</span>
                <span>🛡️</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          0% { opacity: 0; transform: translateY(-100px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          0% { opacity: 0; transform: scale(0.5); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
        }
        .animate-slideDown { animation: slideDown 0.6s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.6s ease-out 0.2s both; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-float { animation: float linear infinite; }
      `}</style>
    </div>
  );
};

// Victory Modal
const VictoryModal = ({ show, xpGained, onClose, wrongTopics, isBossMode }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-amber-900/90 to-yellow-900/90 rounded-3xl p-8 max-w-md w-full border-2 border-amber-400/50 shadow-2xl shadow-amber-500/30 animate-victory my-4">
        <div className="text-center">
          <div className="text-6xl mb-4">{isBossMode ? '👑' : '🎉'}</div>
          <h2 className="text-3xl font-bold text-amber-100 mb-2">{isBossMode ? 'Boss Derrotado!' : 'Vitória!'}</h2>
          <div className="bg-black/20 rounded-xl p-4 mb-2">
            <div className="text-amber-400 text-4xl font-bold">+{xpGained} XP</div>
          </div>
          <BattleReport wrongTopics={wrongTopics || []} />
          <button
            onClick={onClose}
            className="w-full mt-4 py-3 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl transition-all duration-300 hover:scale-105"
          >
            Continuar
          </button>
        </div>
      </div>
      <style>{`
        @keyframes victory {
          0% { opacity: 0; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-victory { animation: victory 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
};

// Defeat Modal
const DefeatModal = ({ show, onRetry, onQuit, wrongTopics }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-red-900/90 to-slate-900/90 rounded-3xl p-8 max-w-md w-full border-2 border-red-500/50 shadow-2xl my-4">
        <div className="text-center">
          <div className="text-6xl mb-4">💀</div>
          <h2 className="text-3xl font-bold text-red-200 mb-2">Derrota!</h2>
          <p className="text-slate-300 mb-2">Não desista! Você vai melhorar!</p>
          <BattleReport wrongTopics={wrongTopics || []} />
          <div className="grid gap-3 mt-4">
            <button
              onClick={onRetry}
              className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl transition-all duration-300 hover:scale-105"
            >
              🔄 Tentar Novamente
            </button>
            <button
              onClick={onQuit}
              className="w-full py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all duration-300"
            >
              📚 Voltar às Lições
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== AUTH FORM COMPONENTS ====================

const LoginForm = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(email, senha); }} className="space-y-4">
      <div>
        <label className="block text-amber-300 font-bold mb-2 text-sm">Email</label>
        <input
          type="email" required value={email} onChange={e => setEmail(e.target.value)}
          placeholder="seu@email.com"
          className="w-full p-3 rounded-xl bg-slate-900/50 border-2 border-slate-600 focus:border-amber-500 outline-none text-white transition-colors"
        />
      </div>
      <div>
        <label className="block text-amber-300 font-bold mb-2 text-sm">Senha</label>
        <input
          type="password" required value={senha} onChange={e => setSenha(e.target.value)}
          placeholder="••••••••"
          className="w-full p-3 rounded-xl bg-slate-900/50 border-2 border-slate-600 focus:border-amber-500 outline-none text-white transition-colors"
        />
      </div>
      <button type="submit" className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] mt-2">
        ⚔️ Entrar na Aventura
      </button>
    </form>
  );
};

const RegisterForm = ({ onSubmit }) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(nome, email, senha); }} className="space-y-4">
      <div>
        <label className="block text-amber-300 font-bold mb-2 text-sm">Nome do Personagem</label>
        <input
          type="text" required maxLength={50} value={nome} onChange={e => setNome(e.target.value)}
          placeholder="Como te chamarão na aventura"
          className="w-full p-3 rounded-xl bg-slate-900/50 border-2 border-slate-600 focus:border-amber-500 outline-none text-white transition-colors"
        />
      </div>
      <div>
        <label className="block text-amber-300 font-bold mb-2 text-sm">Email</label>
        <input
          type="email" required value={email} onChange={e => setEmail(e.target.value)}
          placeholder="seu@email.com"
          className="w-full p-3 rounded-xl bg-slate-900/50 border-2 border-slate-600 focus:border-amber-500 outline-none text-white transition-colors"
        />
      </div>
      <div>
        <label className="block text-amber-300 font-bold mb-2 text-sm">Senha (mín. 6 caracteres)</label>
        <input
          type="password" required minLength={6} value={senha} onChange={e => setSenha(e.target.value)}
          placeholder="••••••••"
          className="w-full p-3 rounded-xl bg-slate-900/50 border-2 border-slate-600 focus:border-amber-500 outline-none text-white transition-colors"
        />
      </div>
      <button type="submit" className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] mt-2">
        ✨ Criar Conta e Jogar
      </button>
    </form>
  );
};

// ==================== LANDING PAGE ====================

const Campfire = () => (
  <>
    <style>{`
      .flame { position: absolute; bottom: 30px; left: 50%; border-radius: 50% 50% 30% 30% / 60% 60% 40% 40%; transform-origin: bottom center; }
      .flame-outer { width: 70px; height: 110px; margin-left: -35px; background: linear-gradient(to top, #f97316, rgba(251,146,60,0.5)); animation: flicker 1.8s ease-in-out infinite; }
      .flame-mid   { width: 50px; height: 80px;  margin-left: -25px; background: linear-gradient(to top, #fbbf24, rgba(249,115,22,0.67)); animation: flicker2 1.4s ease-in-out infinite; }
      .flame-inner { width: 28px; height: 50px;  margin-left: -14px; background: linear-gradient(to top, #fef08a, rgba(251,191,36,0.8)); animation: flicker 1s ease-in-out infinite reverse; }
      .ember-particle { position: absolute; width: 4px; height: 4px; border-radius: 50%; background: #fbbf24; animation: ember 2s ease-out infinite; bottom: 50px; }
      .ground-glow-cf { position: absolute; bottom: -10px; left: 50%; transform: translateX(-50%); width: 140px; height: 35px; background: radial-gradient(ellipse, rgba(251,146,60,0.5) 0%, transparent 70%); animation: glow-pulse-cf 2s ease-in-out infinite; border-radius: 50%; }
      .log-left-cf  { position: absolute; bottom: 10px; left: 8px;  width: 80px; height: 16px; background: #78350f; border-radius: 8px; transform: rotate(-22deg); box-shadow: inset 0 2px 4px rgba(0,0,0,0.4); }
      .log-right-cf { position: absolute; bottom: 10px; right: 8px; width: 80px; height: 16px; background: #92400e; border-radius: 8px; transform: rotate(22deg); box-shadow: inset 0 2px 4px rgba(0,0,0,0.4); }
      @keyframes flicker  { 0%,100%{transform:translateX(-50%) scaleX(1) scaleY(1) rotate(-1deg)} 25%{transform:translateX(-50%) scaleX(.95) scaleY(1.05) rotate(1deg)} 50%{transform:translateX(-50%) scaleX(1.05) scaleY(.97) rotate(-2deg)} 75%{transform:translateX(-50%) scaleX(.98) scaleY(1.03) rotate(1.5deg)} }
      @keyframes flicker2 { 0%,100%{transform:translateX(-50%) scaleX(.9) scaleY(1.1) rotate(2deg)} 50%{transform:translateX(-50%) scaleX(1.1) scaleY(.9) rotate(-2deg)} }
      @keyframes ember { 0%{opacity:0;transform:translateY(0) translateX(0) scale(1)} 20%{opacity:1} 80%{opacity:.6} 100%{opacity:0;transform:translateY(-110px) translateX(var(--dx,0px)) scale(.15)} }
      @keyframes glow-pulse-cf { 0%,100%{opacity:.5;transform:translateX(-50%) scaleX(1)} 50%{opacity:.9;transform:translateX(-50%) scaleX(1.15)} }
      @keyframes title-glow { 0%,100%{text-shadow:0 0 20px rgba(251,191,36,.5),0 0 40px rgba(251,146,60,.2)} 50%{text-shadow:0 0 50px rgba(251,191,36,1),0 0 90px rgba(251,146,60,.6),0 0 130px rgba(234,88,12,.4)} }
      @keyframes fadeInUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
      @keyframes scroll-bounce { 0%,100%{transform:translateY(0) translateX(-50%)} 50%{transform:translateY(10px) translateX(-50%)} }
      @keyframes hero-bg-pulse { 0%,100%{opacity:.4} 50%{opacity:.7} }
    `}</style>
    <div style={{ position: 'relative', width: '160px', height: '190px' }}>
      <div className="log-left-cf" />
      <div className="log-right-cf" />
      <div className="flame flame-outer" />
      <div className="flame flame-mid" />
      <div className="flame flame-inner" />
      {[0,1,2,3,4,5].map(i => (
        <div key={i} className="ember-particle" style={{ '--dx': `${(i - 2.5) * 20}px`, animationDelay: `${i * 0.38}s`, left: `${22 + i * 10}%` }} />
      ))}
      <div className="ground-glow-cf" />
    </div>
  </>
);

const LandingPage = ({ onStart }) => {
  const scrollToStory = () => {
    document.getElementById('story-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const enemies = [
    { emoji: '👹', name: 'Slime da Confusão', desc: 'Embaralha suas palavras e testa seus substantivos' },
    { emoji: '🧟', name: 'Zumbi do Erro', desc: 'Resurge com verbos mal conjugados e concordâncias erradas' },
    { emoji: '🐉', name: 'Dragão da Dúvida', desc: 'Lança chamas de crase e pontuação traiçoeira' },
    { emoji: '👑', name: 'Boss de Fase', desc: 'O guardião final — derrote-o para dominar o capítulo' },
  ];

  const features = [
    { emoji: '🔥', title: 'Sistema de Combo', desc: 'Acerte em sequência para causar dano crescente. Quanto mais acertos seguidos, mais poderoso o golpe!' },
    { emoji: '🛡️', title: 'Escudo Mágico', desc: '3 acertos consecutivos ativam um escudo que absorve o próximo erro sem perder vida.' },
    { emoji: '⚡', title: 'Golpe Crítico', desc: '5 acertos seguidos desencadeiam um crítico devastador com dano dobrado.' },
    { emoji: '📖', title: 'Relatório de Batalha', desc: 'Ao fim de cada batalha, saiba exatamente quais assuntos treinar para a próxima aventura.' },
  ];

  return (
    <div style={{ background: '#020617', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── HERO ── */}
      <section style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', background: 'linear-gradient(to bottom, #020617 0%, #0f172a 50%, rgba(67,20,7,0.4) 100%)', padding: '2rem 1rem' }}>

        {/* Partículas de fundo */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {[...Array(12)].map((_, i) => (
            <div key={i} style={{ position: 'absolute', width: `${2 + (i % 3)}px`, height: `${2 + (i % 3)}px`, borderRadius: '50%', background: `rgba(251,146,60,${0.1 + (i % 4) * 0.08})`, left: `${5 + i * 8}%`, top: `${10 + (i * 37) % 75}%`, animation: `hero-bg-pulse ${2.5 + i * 0.3}s ease-in-out infinite`, animationDelay: `${i * 0.25}s` }} />
          ))}
        </div>

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', textAlign: 'center', maxWidth: '600px' }}>
          <Campfire />

          <div>
            <h1 style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', fontWeight: 900, letterSpacing: '0.05em', background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 40%, #f97316 70%, #fbbf24 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'title-glow 3s ease-in-out infinite', lineHeight: 1.1 }}>
              GRAMÁTICA<br />QUEST
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginTop: '0.75rem', fontStyle: 'italic', animation: 'fadeInUp 1s ease-out 0.3s both' }}>
              O reino das palavras precisa de um guardião
            </p>
          </div>

          <button
            onClick={scrollToStory}
            style={{ marginTop: '0.5rem', padding: '1rem 2.5rem', background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', fontWeight: 700, fontSize: '1.1rem', border: 'none', borderRadius: '9999px', cursor: 'pointer', boxShadow: '0 0 30px rgba(249,115,22,0.5)', animation: 'fadeInUp 1s ease-out 0.6s both', transition: 'transform 0.2s, box-shadow 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.07)'; e.currentTarget.style.boxShadow = '0 0 50px rgba(249,115,22,0.8)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 0 30px rgba(249,115,22,0.5)'; }}
          >
            ⚔️ Descobrir a Lenda
          </button>
        </div>

        {/* Seta bounce */}
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', color: 'rgba(251,146,60,0.6)', fontSize: '1.5rem', animation: 'scroll-bounce 1.8s ease-in-out infinite', cursor: 'pointer' }} onClick={scrollToStory}>
          ▼
        </div>
      </section>

      {/* ── STORYLINE ── */}
      <section id="story-section" style={{ background: '#020617', borderTop: '2px solid', borderImage: 'linear-gradient(90deg, transparent, #f97316, transparent) 1', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2.2rem', fontWeight: 800, color: '#fbbf24', marginBottom: '2.5rem', animation: 'fadeInUp 0.8s ease-out both' }}>
            📜 A Lenda
          </h2>

          <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))', border: '1px solid rgba(251,146,60,0.2)', borderRadius: '1.5rem', padding: '2rem 2.5rem', marginBottom: '3rem', animation: 'fadeInUp 0.8s ease-out 0.2s both' }}>
            <p style={{ color: '#cbd5e1', lineHeight: 1.9, fontSize: '1.05rem', marginBottom: '1.25rem' }}>
              Há muito tempo, o <strong style={{ color: '#fbbf24' }}>Reino da Gramática</strong> vivia em harmonia. As palavras dançavam em frases perfeitas, os verbos concordavam com seus sujeitos, e a crase era respeitada por todos.
            </p>
            <p style={{ color: '#cbd5e1', lineHeight: 1.9, fontSize: '1.05rem' }}>
              Mas um dia, o temível <strong style={{ color: '#f97316' }}>Dragão da Dúvida</strong> lançou uma maldição sombria. Agora substantivos vagam sem sentido, verbos recusam conjugar, e o caos gramatical se espalha por cada frase. Só um verdadeiro <strong style={{ color: '#fbbf24' }}>Guardião da Gramática</strong> pode restaurar a ordem...
            </p>
          </div>

          <h3 style={{ textAlign: 'center', color: '#94a3b8', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '1.5rem' }}>
            Inimigos que você vai enfrentar
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {enemies.map((e, i) => (
              <div key={i} style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(251,146,60,0.15)', borderRadius: '1rem', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'flex-start', gap: '1rem', animation: 'fadeInUp 0.8s ease-out both', animationDelay: `${0.1 + i * 0.12}s`, transition: 'border-color 0.3s, transform 0.3s' }}
                onMouseEnter={el => { el.currentTarget.style.borderColor = 'rgba(251,146,60,0.5)'; el.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={el => { el.currentTarget.style.borderColor = 'rgba(251,146,60,0.15)'; el.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <span style={{ fontSize: '2.5rem', lineHeight: 1 }}>{e.emoji}</span>
                <div>
                  <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: '0.25rem' }}>{e.name}</div>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.5 }}>{e.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ background: 'linear-gradient(to bottom, #020617, #0f172a)', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2.2rem', fontWeight: 800, color: '#fbbf24', marginBottom: '0.75rem' }}>
            ⚔️ Sua Jornada
          </h2>
          <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '3rem', fontSize: '1rem' }}>
            Mecânicas que tornam cada batalha única
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '4rem' }}>
            {features.map((f, i) => (
              <div key={i} style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9), rgba(15,23,42,0.9))', border: '1px solid rgba(251,146,60,0.15)', borderRadius: '1rem', padding: '1.75rem', animation: 'fadeInUp 0.8s ease-out both', animationDelay: `${i * 0.12}s`, transition: 'border-color 0.3s, transform 0.3s' }}
                onMouseEnter={el => { el.currentTarget.style.borderColor = 'rgba(251,146,60,0.5)'; el.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={el => { el.currentTarget.style.borderColor = 'rgba(251,146,60,0.15)'; el.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{f.emoji}</div>
                <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.5rem' }}>{f.title}</div>
                <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>

          {/* CTA Final */}
          <div style={{ textAlign: 'center', padding: '3rem 1.5rem', background: 'linear-gradient(135deg, rgba(249,115,22,0.1), rgba(234,88,12,0.05))', border: '1px solid rgba(249,115,22,0.3)', borderRadius: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔥</div>
            <h3 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: 800, marginBottom: '0.75rem' }}>
              Pronto para a batalha?
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: '2rem', fontSize: '1rem' }}>
              Crie sua conta gratuita e salve seu progresso em qualquer dispositivo
            </p>
            <button
              onClick={onStart}
              style={{ padding: '1.1rem 3rem', background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', fontWeight: 800, fontSize: '1.15rem', border: 'none', borderRadius: '9999px', cursor: 'pointer', boxShadow: '0 0 40px rgba(249,115,22,0.5)', transition: 'transform 0.2s, box-shadow 0.2s', letterSpacing: '0.02em' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 0 60px rgba(249,115,22,0.8)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.5)'; }}
            >
              ✨ Começar Minha Aventura
            </button>
            <p style={{ color: '#475569', fontSize: '0.8rem', marginTop: '1rem' }}>
              Gratuito · Sem anúncios · Progresso na nuvem
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== PROFILE SELECT SCREEN ====================
function ProfileSelectScreen({ authToken, profileList, setProfileList, profileListLoading, setProfileListLoading, profileListError, setProfileListError, onSelect, onCreateNew, onLogout, apiListProfiles, apiDeleteProfile }) {
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!authToken) return;
    setProfileListLoading(true);
    setProfileListError('');
    apiListProfiles(authToken)
      .then(data => setProfileList(data.profiles || []))
      .catch(err => setProfileListError(err.message))
      .finally(() => setProfileListLoading(false));
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (profileId) => {
    try {
      await apiDeleteProfile(authToken, profileId);
      setProfileList(prev => prev.filter(p => p.id !== profileId));
      setConfirmDelete(null);
    } catch (err) {
      setProfileListError(err.message);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl mx-auto">
      <div className="text-center py-8">
        <div className="text-8xl mb-4">👥</div>
        <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200 bg-clip-text text-transparent">
          Selecione seu Personagem
        </h2>
        <p className="text-slate-400">Escolha um aventureiro ou crie um novo!</p>
      </div>

      {profileListError && (
        <div style={{ padding: '0.75rem', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.75rem', color: '#fca5a5', fontSize: '0.875rem', textAlign: 'center' }}>
          ⚠️ {profileListError}
        </div>
      )}

      <div className="space-y-3">
        {profileListLoading ? (
          <div className="text-center py-8 text-slate-400">Carregando personagens...</div>
        ) : (
          profileList.map(profile => (
            <div key={profile.id} className="relative group">
              {confirmDelete === profile.id ? (
                <div className="bg-red-900/30 border border-red-500/50 rounded-2xl p-5 flex items-center justify-between gap-4">
                  <p className="text-red-300 text-sm">Deletar <strong>{profile.name}</strong>? Esta ação não pode ser desfeita.</p>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setConfirmDelete(null)} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white">Cancelar</button>
                    <button onClick={() => handleDelete(profile.id)} className="px-3 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-sm text-white font-bold">Deletar</button>
                  </div>
                </div>
              ) : (
                <div
                  className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 rounded-2xl p-5 border border-amber-500/30 hover:border-amber-400 backdrop-blur-sm transition-all duration-300 cursor-pointer"
                  onClick={() => onSelect(profile)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">⚔️</span>
                      <div>
                        <h3 className="text-xl font-bold text-amber-300">{profile.name}</h3>
                        <p className="text-sm text-slate-400">Nível {profile.level} • {profile.xp} XP</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(profile.id); }}
                      className="opacity-0 group-hover:opacity-100 p-2 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-400 transition-all text-sm"
                      title="Deletar personagem"
                    >🗑️</button>
                  </div>
                  <div className="flex gap-4 text-sm text-slate-400 mt-2 ml-12">
                    <span>📚 {profile.stats?.lessonsCompleted || 0} lições</span>
                    <span>⚔️ {profile.stats?.enemiesDefeated || 0} vitórias</span>
                    <span>🎯 {profile.stats?.accuracy || 0}% precisão</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}

        {!profileListLoading && profileList.length < 3 && (
          <button
            onClick={onCreateNew}
            className="w-full p-4 rounded-2xl border-2 border-dashed border-slate-600 hover:border-amber-500/50 bg-slate-800/30 hover:bg-slate-800/60 transition-all duration-300 text-slate-400 hover:text-amber-300 font-medium"
          >
            ✨ Criar novo personagem {profileList.length > 0 && `(${profileList.length}/3)`}
          </button>
        )}

        <button
          onClick={onLogout}
          className="w-full p-4 rounded-2xl border border-slate-600/50 hover:border-red-500/50 bg-slate-800/50 hover:bg-red-500/10 transition-all duration-300 text-slate-400 hover:text-red-300 font-medium text-sm"
        >
          🚪 Sair e trocar de conta
        </button>
      </div>
    </div>
  );
}

// ==================== CREATE CHARACTER SCREEN ====================
function CreateCharacterScreen({ authToken, onCreated, onBack, apiCreateProfile }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiCreateProfile(authToken, trimmed);
      onCreated(data.profile);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl mx-auto">
      <div className="text-center py-8">
        <div className="text-8xl mb-4">✨</div>
        <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200 bg-clip-text text-transparent">
          Crie seu Personagem
        </h2>
        <p className="text-slate-400">Escolha um nome para sua jornada gramatical!</p>
      </div>

      <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 rounded-3xl p-8 border border-slate-600/30 backdrop-blur-sm">
        <div className="space-y-6">
          {error && (
            <div style={{ padding: '0.75rem', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.75rem', color: '#fca5a5', fontSize: '0.875rem', textAlign: 'center' }}>
              ⚠️ {error}
            </div>
          )}

          <div>
            <label className="block text-amber-300 font-bold mb-3 text-lg">Nome do Personagem</label>
            <input
              type="text"
              placeholder="Digite seu nome..."
              maxLength={50}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
              className="w-full p-4 rounded-xl bg-slate-900/50 border-2 border-slate-600 focus:border-amber-500 outline-none text-white text-xl transition-colors"
              autoFocus
            />
            <p className="text-sm text-slate-500 mt-2">Este nome será usado nas histórias e diálogos do jogo</p>
          </div>

          <div className="bg-purple-900/20 rounded-xl p-5 border border-purple-500/20 flex items-start gap-3">
            <span className="text-3xl">🔮</span>
            <p className="text-slate-300 text-sm">Escolha um nome que você goste! Ele aparecerá em toda a sua aventura.</p>
          </div>

          <div className="flex gap-4">
            <button
              onClick={onBack}
              className="flex-1 py-4 px-6 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all duration-300"
            >
              ← Voltar
            </button>
            <button
              onClick={handleCreate}
              disabled={!name.trim() || loading}
              className="flex-1 py-4 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold rounded-xl transition-all duration-300 hover:scale-[1.02]"
            >
              {loading ? 'Criando...' : 'Começar Aventura! →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN GAME COMPONENT ====================
export default function GramaticaQuest() {
  // Profile state
  const [currentProfile, setCurrentProfile] = useState(null);
  const [playerName, setPlayerName] = useState('');


  // Game state
  const [gameData, setGameData] = useState({
    xp: 0,
    level: 1,
    badges: [],
    completedChapters: {},
    stats: {
      questionsAnswered: 0,
      correctAnswers: 0,
      accuracy: 0,
      lessonsCompleted: 0,
      enemiesDefeated: 0,
    }
  });
  
  // Phase/Lesson state
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [practiceState, setPracticeState] = useState(null);
  
  // Battle state
  const [currentEnemy, setCurrentEnemy] = useState(null);
  const [enemyHP, setEnemyHP] = useState(0);
  const [lives, setLives] = useState(3);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isEnemyHit, setIsEnemyHit] = useState(false);
  const [isEnemyDefeated, setIsEnemyDefeated] = useState(false);
  const [battleXP, setBattleXP] = useState(0);
  const [comboCount, setComboCount] = useState(0);
  const [hasShield, setHasShield] = useState(false);
  const [damageFloat, setDamageFloat] = useState(null);
  const [wrongTopics, setWrongTopics] = useState([]);
  const [battleQuestions, setBattleQuestions] = useState([]);
  const [isBossMode, setIsBossMode] = useState(false);
  
  // Modal state
  const [showVictory, setShowVictory] = useState(false);
  const [showDefeat, setShowDefeat] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpData, setLevelUpData] = useState({ level: 1, title: '' });

  // Auth state
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('gq-token'));
  const [authUser, setAuthUser] = useState(null); // eslint-disable-line no-unused-vars
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authTab, setAuthTab] = useState('login'); // 'login' | 'register'

  // Profile selection state
  const [profileList, setProfileList] = useState([]);
  const [profileListLoading, setProfileListLoading] = useState(false);
  const [profileListError, setProfileListError] = useState('');

  const saveTimerRef = useRef(null);

  // Music state
  const [isMusicPlaying, setIsMusicPlaying] = useState(() => {
    const saved = localStorage.getItem('gramatica-quest-music');
    return saved ? JSON.parse(saved) : false;
  });
  const audioRef = useRef(null);

  // Routing
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const ROUTE_MAP = {
    '/':                'landing',
    '/login':           'authScreen',
    '/personagens':     'profileSelect',
    '/novo-personagem': 'createCharacter',
    '/menu':            'menu',
    '/mapa':            'map',
    '/fase':            'phase',
    '/licao':           'lesson',
    '/batalha':         'battle',
  };
  const screen = ROUTE_MAP[pathname] ?? 'authScreen';

  // Get current title
  const getCurrentTitle = (level) => {
    const titleData = TITLES.find((t, i) => i === level - 1) || TITLES[0];
    return titleData.name;
  };

  // Calculate level from XP
  const calculateLevel = (xp) => {
    for (let i = TITLES.length - 1; i >= 0; i--) {
      if (xp >= TITLES[i].xp) {
        return i + 1;
      }
    }
    return 1;
  };

  // ==================== AUTH FUNCTIONS ====================

  // Carrega um personagem selecionado e entra no jogo
  const loadCharacter = (userData, profileData, statsData) => {
    setAuthUser(userData);
    setPlayerName(profileData.name ?? userData.nome);
    setCurrentProfile(profileData.id);
    localStorage.setItem('gq-profile-id', profileData.id);
    setGameData({
      xp: profileData.xp ?? 0,
      level: profileData.level ?? 1,
      badges: profileData.badges ?? [],
      completedChapters: profileData.completedChapters ?? {},
      stats: {
        questionsAnswered: statsData.questionsAnswered ?? 0,
        correctAnswers: statsData.correctAnswers ?? 0,
        accuracy: statsData.accuracy ?? 0,
        lessonsCompleted: statsData.lessonsCompleted ?? 0,
        enemiesDefeated: statsData.enemiesDefeated ?? 0,
      }
    });
    navigate('/menu');
  };

  // Após login/register: armazena token e vai para seleção de personagem
  const loadAuthProfile = (userData) => {
    setAuthUser(userData);
    setPlayerName('');
    setCurrentProfile(null);
    navigate('/personagens');
  };

  // Login
  const handleLogin = async (email, senha) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const data = await apiLogin(email, senha);
      localStorage.setItem('gq-token', data.token);
      setAuthToken(data.token);
      loadAuthProfile(data.user);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Registro
  const handleRegister = async (nome, email, senha) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const data = await apiRegister(nome, email, senha);
      localStorage.setItem('gq-token', data.token);
      setAuthToken(data.token);
      loadAuthProfile(data.user);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('gq-token');
    localStorage.removeItem('gq-profile-id');
    setAuthToken(null);
    setAuthUser(null);
    setCurrentProfile(null);
    setPlayerName('');
    navigate('/login');
  };

  // Ao abrir o app com token + profileId salvos, recarrega o personagem automaticamente
  useEffect(() => {
    const savedProfileId = localStorage.getItem('gq-profile-id');
    if (authToken && pathname === '/login') {
      setAuthLoading(true);
      if (savedProfileId) {
        // Tenta carregar o último personagem usado
        apiGetProfile(authToken, parseInt(savedProfileId))
          .then(data => loadCharacter(data.user, data.profile, data.stats))
          .catch((err) => {
            // Token expirado → faz logout; personagem deletado → vai para seleção
            if (err.message.includes('expirado') || err.message.includes('inválido')) {
              localStorage.removeItem('gq-token');
              localStorage.removeItem('gq-profile-id');
              setAuthToken(null);
            } else {
              // Profile não encontrado: mantém token, vai para seleção de personagem
              localStorage.removeItem('gq-profile-id');
              navigate('/personagens');
            }
          })
          .finally(() => setAuthLoading(false));
      } else {
        // Tem token mas não tem profile salvo: vai direto para seleção de personagem
        navigate('/personagens');
        setAuthLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save com debounce de 2s para não sobrecarregar a API
  useEffect(() => {
    if (!authToken || !currentProfile) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      apiSaveProfile(authToken, currentProfile, gameData).catch(() => {});
    }, 2000);
    return () => clearTimeout(saveTimerRef.current);
  }, [gameData, authToken, currentProfile]);

  // Music control
  const toggleMusic = () => {
    const newState = !isMusicPlaying;
    setIsMusicPlaying(newState);
    localStorage.setItem('gramatica-quest-music', JSON.stringify(newState));

    if (audioRef.current) {
      if (newState) {
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      } else {
        audioRef.current.pause();
      }
    }
  };

  // Handle music playback
  useEffect(() => {
    if (audioRef.current) {
      if (isMusicPlaying) {
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isMusicPlaying]);

  // Música toca apenas na tela de seleção de personagem
  useEffect(() => {
    if (pathname === '/personagens') {
      if (!isMusicPlaying) {
        setIsMusicPlaying(true);
        localStorage.setItem('gramatica-quest-music', 'true');
      }
    } else {
      if (isMusicPlaying) {
        setIsMusicPlaying(false);
        localStorage.setItem('gramatica-quest-music', 'false');
        if (audioRef.current) audioRef.current.pause();
      }
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start a chapter (lesson)
  const startChapter = (chapter) => {
    setCurrentChapter(chapter);
    setCurrentPageIndex(0);
    setPracticeState(null);
    navigate('/licao');
    playSound('page');
  };

  // Handle practice answer in lessons
  const handlePracticeAnswer = (index, isCorrect) => {
    playSound(isCorrect ? 'correct' : 'wrong');
    setPracticeState({ selected: index, answered: true, correct: isCorrect });

    const page = currentChapter.pages[currentPageIndex];
    if (isCorrect && page.xp) {
      const oldLevel = gameData.level;
      const newXP = gameData.xp + page.xp;
      const newLevel = calculateLevel(newXP);

      setGameData(prev => ({
        ...prev,
        xp: newXP,
        level: newLevel,
        stats: {
          ...prev.stats,
          questionsAnswered: prev.stats.questionsAnswered + 1,
          correctAnswers: prev.stats.correctAnswers + 1,
          accuracy: Math.round(((prev.stats.correctAnswers + 1) / (prev.stats.questionsAnswered + 1)) * 100),
        }
      }));

      // Check if leveled up
      if (newLevel > oldLevel) {
        const newTitle = getCurrentTitle(newLevel);
        setLevelUpData({ level: newLevel, title: newTitle });
        setShowLevelUp(true);
      }
    }
  };

  // Go to next page in lesson
  const nextPage = () => {
    playSound('page');
    setPracticeState(null);
    
    if (currentPageIndex < currentChapter.pages.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
    } else {
      // Chapter completed
      const chapterKey = `${currentPhase.id}-${currentChapter.id}`;
      setGameData(prev => ({
        ...prev,
        completedChapters: { ...prev.completedChapters, [chapterKey]: true },
        stats: { ...prev.stats, lessonsCompleted: prev.stats.lessonsCompleted + 1 }
      }));
      playSound('levelup');
      navigate('/fase');
    }
  };

  // Shared battle reset helper
  const resetBattleState = () => {
    setLives(3);
    setBattleXP(0);
    setQuestionIndex(0);
    setShowHint(false);
    setIsEnemyDefeated(false);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setComboCount(0);
    setHasShield(false);
    setDamageFloat(null);
    setWrongTopics([]);
  };

  // Start normal battle
  const startBattle = () => {
    resetBattleState();
    setIsBossMode(false);
    navigate('/batalha');

    const enemyType = currentPhase.id <= 2 ? 'slime' : currentPhase.id <= 4 ? 'zombie' : 'dragon';
    const enemy = ENEMIES[enemyType];
    setCurrentEnemy(enemy);
    setEnemyHP(enemy.hp);

    const lesson = LESSONS[currentPhase.id];
    const questions = lesson?.battleQuestions || [];
    const shuffled = [...questions].sort(() => Math.random() - 0.5);
    setBattleQuestions(shuffled);
    setCurrentQuestion(shuffled[0]);
  };

  // Start boss battle
  const startBossMode = () => {
    resetBattleState();
    setIsBossMode(true);
    navigate('/batalha');

    const enemy = ENEMIES['boss'];
    setCurrentEnemy(enemy);
    setEnemyHP(enemy.hp);

    const lesson = LESSONS[currentPhase.id];
    const questions = lesson?.battleQuestions || [];
    const shuffled = [...questions].sort(() => Math.random() - 0.5).slice(0, 10);
    setBattleQuestions(shuffled);
    setCurrentQuestion(shuffled[0]);
  };

  // Handle battle answer
  const handleBattleAnswer = (answerIndex) => {
    if (isAnswered) return;

    setSelectedAnswer(answerIndex);
    setIsAnswered(true);

    const isCorrect = answerIndex === currentQuestion.answer;

    if (isCorrect) {
      playSound('correct');
      setIsEnemyHit(true);
      setTimeout(() => setIsEnemyHit(false), 300);

      const newCombo = comboCount + 1;
      const baseDamage = currentQuestion.difficulty === 'basic' ? 5 :
                         currentQuestion.difficulty === 'intermediate' ? 7 : 10;
      const isCrit = newCombo >= 5;
      const multiplier = isCrit ? 2 : (1 + (newCombo - 1) * 0.1);
      const damage = Math.round(baseDamage * multiplier);
      const newHP = Math.max(0, enemyHP - damage);

      setDamageFloat({ value: damage, isCrit, id: Date.now() });

      // Escudo a cada 3 acertos consecutivos (sem já ter escudo)
      if (newCombo === 3 && !hasShield) {
        setHasShield(true);
        setComboCount(0);
      } else if (isCrit) {
        setComboCount(0);
      } else {
        setComboCount(newCombo);
      }

      setEnemyHP(newHP);
      setBattleXP(prev => prev + currentQuestion.xp);

      setGameData(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          questionsAnswered: prev.stats.questionsAnswered + 1,
          correctAnswers: prev.stats.correctAnswers + 1,
          accuracy: Math.round(((prev.stats.correctAnswers + 1) / (prev.stats.questionsAnswered + 1)) * 100),
        }
      }));

      const isLastBossQuestion = isBossMode && questionIndex >= battleQuestions.length - 1;

      if (newHP <= 0 || isLastBossQuestion) {
        setIsEnemyDefeated(true);
        playSound('levelup');

        setTimeout(() => {
          const oldLevel = gameData.level;
          const totalXP = battleXP + currentQuestion.xp + currentEnemy.xpReward;
          const newTotalXP = gameData.xp + totalXP;
          const newLevel = calculateLevel(newTotalXP);

          setGameData(prev => ({
            ...prev,
            xp: newTotalXP,
            level: newLevel,
            stats: { ...prev.stats, enemiesDefeated: prev.stats.enemiesDefeated + 1 }
          }));

          setBattleXP(totalXP);
          if (authToken && currentProfile && currentPhase) {
            apiBattleSave(authToken, currentProfile, {
              phaseId: currentPhase.id,
              isBoss: isBossMode ? 1 : 0,
              resultado: 'vitoria',
              xpGained: totalXP,
              wrongTopics
            }).catch(() => {});
          }
          setShowVictory(true);

          if (newLevel > oldLevel) {
            setTimeout(() => {
              const newTitle = getCurrentTitle(newLevel);
              setLevelUpData({ level: newLevel, title: newTitle });
              setShowVictory(false);
              setShowLevelUp(true);
            }, 1500);
          }
        }, 1000);
      }
    } else {
      playSound('wrong');
      setComboCount(0);

      if (currentQuestion.topic) {
        setWrongTopics(prev =>
          prev.includes(currentQuestion.topic) ? prev : [...prev, currentQuestion.topic]
        );
      }

      if (hasShield) {
        // Escudo absorve o erro
        setHasShield(false);
      } else {
        setLives(prev => prev - 1);

        setGameData(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            questionsAnswered: prev.stats.questionsAnswered + 1,
            accuracy: Math.round((prev.stats.correctAnswers / (prev.stats.questionsAnswered + 1)) * 100),
          }
        }));

        if (lives <= 1) {
          if (authToken && currentProfile && currentPhase) {
            apiBattleSave(authToken, currentProfile, {
              phaseId: currentPhase.id,
              isBoss: isBossMode ? 1 : 0,
              resultado: 'derrota',
              xpGained: battleXP,
              wrongTopics
            }).catch(() => {});
          }
          setTimeout(() => setShowDefeat(true), 1000);
        }
      }
    }
  };

  // Next battle question
  const nextBattleQuestion = () => {
    if (lives <= 0) return;
    if (!isBossMode && enemyHP <= 0) return;

    const nextIndex = questionIndex + 1;
    if (nextIndex >= battleQuestions.length) return;

    setQuestionIndex(nextIndex);
    setCurrentQuestion(battleQuestions[nextIndex]);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setShowHint(false);
    setDamageFloat(null);
  };

  // Get completed chapters for a phase
  const getCompletedChaptersCount = (phaseId) => {
    const lesson = LESSONS[phaseId];
    if (!lesson) return 0;
    return lesson.chapters.filter(ch => gameData.completedChapters[`${phaseId}-${ch.id}`]).length;
  };

  // ==================== RENDER ====================

  // Landing page: completamente fora do shell do jogo
  if (pathname === '/') {
    return <LandingPage onStart={() => navigate('/login')} />;
  }

  // Guard de autenticação: telas do jogo requerem login
  const PROTECTED_PATHS = ['/personagens', '/novo-personagem', '/menu', '/mapa', '/fase', '/licao', '/batalha'];
  if (PROTECTED_PATHS.includes(pathname) && !authToken) {
    return <Navigate to="/login" replace />;
  }

  // Guards para evitar tela branca ao acessar URL diretamente
  if (pathname === '/fase' && !currentPhase) return <Navigate to="/mapa" replace />;
  if (pathname === '/licao' && !currentChapter) return <Navigate to="/fase" replace />;
  if (pathname === '/batalha' && !currentEnemy) return <Navigate to="/fase" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white overflow-x-hidden">
      <ParticleBackground />
      
      {/* Header */}
      <header className="relative z-10 p-4 bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Logo épica de RPG */}
            <div className="relative group">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-800/80 via-amber-700/70 to-amber-900/80 flex items-center justify-center border-2 border-amber-600/40 shadow-lg shadow-amber-900/20 relative overflow-hidden transition-all duration-300 group-hover:border-amber-500/60 group-hover:shadow-amber-700/30">
                {/* Brilho sutil */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {/* Ícone principal */}
                <div className="relative z-10 flex flex-col items-center justify-center">
                  <span className="text-3xl drop-shadow-md">📜</span>
                  <span className="text-xs font-bold text-amber-950 -mt-1">⚔️</span>
                </div>
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent tracking-wide drop-shadow-lg flex items-center gap-2">
                <span className="text-xl">⚔️</span>
                GRAMÁTICA QUEST
                <span className="text-xl">🛡️</span>
              </h1>
              <p className="text-xs text-amber-400/80 font-medium tracking-wider">
                {playerName ? `${playerName} • A Jornada Gramatical` : '✨ A Jornada Gramatical ✨'}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {screen !== 'authScreen' && (
              <button
                onClick={() => toggleMusic()}
                className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors text-sm"
                title={isMusicPlaying ? 'Pausar música' : 'Tocar música'}
              >
                {isMusicPlaying ? '🔊' : '🔇'}
              </button>
            )}
            {playerName && screen !== 'authScreen' && screen !== 'profileSelect' && (
              <button
                onClick={() => { playSound('click'); handleLogout(); }}
                className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors text-sm"
                title="Sair da conta"
              >
                🚪
              </button>
            )}
            {screen !== 'authScreen' && screen !== 'profileSelect' && screen !== 'createCharacter' && (
              <button
                onClick={() => { playSound('click'); navigate('/personagens'); }}
                className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
                title="Seleção de personagem"
              >
                🏠
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto p-4 pb-20">

        {/* ========== LOGIN / REGISTER SCREEN ========== */}
        {screen === 'authScreen' && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, overflow: 'hidden', background: 'linear-gradient(to bottom, #020617 0%, #0f172a 40%, #1c0a00 100%)' }}>

            {/* Fogueira grande de fundo */}
            <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
              {/* Glow gigante no chão */}
              <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '600px', height: '200px', background: 'radial-gradient(ellipse, rgba(251,146,60,0.25) 0%, transparent 70%)', animation: 'glow-pulse-cf 2.5s ease-in-out infinite', borderRadius: '50%' }} />
              {/* Troncos grandes */}
              <div style={{ position: 'absolute', bottom: '30px', left: 'calc(50% - 140px)', width: '180px', height: '22px', background: 'linear-gradient(90deg, #78350f, #92400e)', borderRadius: '11px', transform: 'rotate(-18deg)', boxShadow: 'inset 0 3px 6px rgba(0,0,0,0.5)' }} />
              <div style={{ position: 'absolute', bottom: '30px', left: 'calc(50% - 40px)', width: '180px', height: '22px', background: 'linear-gradient(90deg, #92400e, #78350f)', borderRadius: '11px', transform: 'rotate(18deg)', boxShadow: 'inset 0 3px 6px rgba(0,0,0,0.5)' }} />
              {/* Brasa */}
              <div style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)', width: '80px', height: '24px', background: 'radial-gradient(ellipse, #fbbf24 0%, #f97316 50%, transparent 80%)', borderRadius: '50%', animation: 'glow-pulse-cf 1.2s ease-in-out infinite' }} />
              {/* Chamas grandes */}
              <div className="flame" style={{ width: '160px', height: '280px', marginLeft: '-80px', background: 'linear-gradient(to top, #ea580c, rgba(251,146,60,0.4))', animation: 'flicker 1.8s ease-in-out infinite', bottom: '52px' }} />
              <div className="flame" style={{ width: '110px', height: '210px', marginLeft: '-55px', background: 'linear-gradient(to top, #f97316, rgba(251,191,36,0.5))', animation: 'flicker2 1.4s ease-in-out infinite', bottom: '52px' }} />
              <div className="flame" style={{ width: '70px', height: '150px', marginLeft: '-35px', background: 'linear-gradient(to top, #fbbf24, rgba(254,240,138,0.7))', animation: 'flicker 1s ease-in-out infinite reverse', bottom: '52px' }} />
              <div className="flame" style={{ width: '35px', height: '90px', marginLeft: '-17px', background: 'linear-gradient(to top, #fef08a, rgba(255,255,255,0.6))', animation: 'flicker2 0.8s ease-in-out infinite', bottom: '52px' }} />
              {/* Faíscas/brasas subindo — mais numerosas */}
              {[...Array(18)].map((_, i) => (
                <div key={i} className="ember-particle" style={{
                  '--dx': `${(i - 8.5) * 22}px`,
                  animationDelay: `${i * 0.22}s`,
                  animationDuration: `${2 + (i % 4) * 0.5}s`,
                  left: `calc(50% + ${(i - 8.5) * 8}px)`,
                  bottom: '60px',
                  width: `${3 + (i % 3)}px`,
                  height: `${3 + (i % 3)}px`,
                  background: i % 3 === 0 ? '#fef08a' : i % 3 === 1 ? '#fbbf24' : '#f97316',
                  position: 'absolute',
                  borderRadius: '50%',
                  animation: `ember ${2 + (i % 4) * 0.5}s ease-out infinite`,
                }} />
              ))}
            </div>

            {/* Conteúdo centralizado */}
            <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem 1rem' }}>
              <div style={{ width: '100%', maxWidth: '420px' }}>
                {/* Título */}
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '2.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #fef3c7, #fbbf24, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'title-glow 3s ease-in-out infinite', letterSpacing: '0.05em', lineHeight: 1.1 }}>
                    GRAMÁTICA<br />QUEST
                  </h2>
                  <p style={{ color: '#94a3b8', marginTop: '0.5rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    O reino das palavras aguarda seu guardião
                  </p>
                </div>

                {authLoading ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div style={{ fontSize: '3rem', animation: 'flicker 1s ease-in-out infinite' }}>🔥</div>
                    <p style={{ color: '#fbbf24', marginTop: '1rem' }}>Carregando...</p>
                  </div>
                ) : (
                  <div style={{ background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(16px)', borderRadius: '1.5rem', padding: '2rem', border: '1px solid rgba(251,146,60,0.25)', boxShadow: '0 0 60px rgba(251,146,60,0.1)' }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', marginBottom: '1.5rem', background: 'rgba(2,6,23,0.6)', borderRadius: '0.75rem', padding: '4px', gap: '4px' }}>
                      <button
                        onClick={() => { setAuthTab('login'); setAuthError(''); }}
                        style={{ flex: 1, padding: '0.6rem', borderRadius: '0.6rem', fontWeight: 700, fontSize: '0.95rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: authTab === 'login' ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'transparent', color: authTab === 'login' ? '#fff' : '#64748b', boxShadow: authTab === 'login' ? '0 0 20px rgba(249,115,22,0.4)' : 'none' }}
                      >
                        Entrar
                      </button>
                      <button
                        onClick={() => { setAuthTab('register'); setAuthError(''); }}
                        style={{ flex: 1, padding: '0.6rem', borderRadius: '0.6rem', fontWeight: 700, fontSize: '0.95rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: authTab === 'register' ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'transparent', color: authTab === 'register' ? '#fff' : '#64748b', boxShadow: authTab === 'register' ? '0 0 20px rgba(249,115,22,0.4)' : 'none' }}
                      >
                        Criar Conta
                      </button>
                    </div>

                    {authError && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.75rem', color: '#fca5a5', fontSize: '0.875rem', textAlign: 'center' }}>
                        ⚠️ {authError}
                      </div>
                    )}

                    {authTab === 'login' ? (
                      <LoginForm onSubmit={handleLogin} />
                    ) : (
                      <RegisterForm onSubmit={handleRegister} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== PROFILE SELECT SCREEN ========== */}
        {screen === 'profileSelect' && (
          <ProfileSelectScreen
            authToken={authToken}
            profileList={profileList}
            setProfileList={setProfileList}
            profileListLoading={profileListLoading}
            setProfileListLoading={setProfileListLoading}
            profileListError={profileListError}
            setProfileListError={setProfileListError}
            onSelect={(profile) => {
              playSound('click');
              loadCharacter({ nome: profile.name }, profile, profile.stats);
            }}
            onCreateNew={() => { playSound('click'); navigate('/novo-personagem'); }}
            onLogout={() => { playSound('click'); handleLogout(); }}
            apiListProfiles={apiListProfiles}
            apiDeleteProfile={apiDeleteProfile}
          />
        )}

        {/* ========== CREATE CHARACTER SCREEN ========== */}
        {screen === 'createCharacter' && (
          <CreateCharacterScreen
            authToken={authToken}
            onCreated={(profile) => {
              playSound('click');
              loadCharacter({ nome: profile.name }, profile, profile.stats);
            }}
            onBack={() => { playSound('click'); navigate('/personagens'); }}
            apiCreateProfile={apiCreateProfile}
          />
        )}

        {/* ========== MENU SCREEN ========== */}
        {screen === 'menu' && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center py-8">
              <div className="text-8xl mb-4">📚</div>
              <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200 bg-clip-text text-transparent">
                Bem-vindo, {playerName}!
              </h2>
              <p className="text-slate-400">Aprenda gramática de forma divertida e imersiva!</p>
            </div>
            
            <XPBar 
              currentXP={gameData.xp} 
              level={gameData.level}
              title={getCurrentTitle(gameData.level)}
            />
            
            <StatsDashboard stats={gameData.stats} />
            
            <div className="grid gap-4 mt-8">
              <button
                onClick={() => { playSound('click'); navigate('/mapa'); }}
                className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl text-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3"
              >
                <span className="text-2xl">🗺️</span>
                COMEÇAR JORNADA
              </button>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-4 mt-6">
              <h3 className="font-bold text-amber-300 mb-2">📖 Como Funciona:</h3>
              <ol className="text-slate-300 text-sm space-y-2">
                <li>1️⃣ Escolha uma fase no mapa</li>
                <li>2️⃣ Leia as lições com calma - elas ensinam tudo!</li>
                <li>3️⃣ Pratique com exercícios durante as lições</li>
                <li>4️⃣ Enfrente o monstro em batalha para ganhar XP</li>
                <li>5️⃣ Suba de nível e desbloqueie novas fases!</li>
              </ol>
            </div>
          </div>
        )}

        {/* ========== MAP SCREEN ========== */}
        {screen === 'map' && (
          <div className="space-y-4 animate-fadeIn">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => { playSound('click'); navigate('/menu'); }}
                className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
              >
                ← Voltar
              </button>
              <h2 className="text-2xl font-bold">🗺️ Reino da Linguagem</h2>
            </div>
            
            <XPBar 
              currentXP={gameData.xp} 
              level={gameData.level}
              title={getCurrentTitle(gameData.level)}
            />
            
            <div className="grid gap-4 mt-6">
              {PHASES.map(phase => {
                const isUnlocked = gameData.level >= phase.unlockLevel;
                const lesson = LESSONS[phase.id];
                const totalChapters = lesson?.chapters?.length || 0;
                const completedChapters = getCompletedChaptersCount(phase.id);
                const isCompleted = totalChapters > 0 && completedChapters === totalChapters;
                
                return (
                  <PhaseCard
                    key={phase.id}
                    phase={phase}
                    isUnlocked={isUnlocked}
                    isCompleted={isCompleted}
                    lessonsCompleted={completedChapters}
                    totalLessons={totalChapters}
                    onClick={() => {
                      if (isUnlocked) {
                        playSound('click');
                        setCurrentPhase(phase);
                        navigate('/fase');
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* ========== PHASE SCREEN ========== */}
        {screen === 'phase' && currentPhase && (
          <div className="space-y-4 animate-fadeIn">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => { playSound('click'); navigate('/mapa'); }}
                className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
              >
                ← Mapa
              </button>
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  {currentPhase.icon} {currentPhase.name}
                </h2>
                <p className="text-slate-400 text-sm">{currentPhase.subtitle}</p>
              </div>
            </div>
            
            {/* Phase Intro */}
            {LESSONS[currentPhase.id]?.intro && (
              <NarratorDialog
                narrator={LESSONS[currentPhase.id].intro.narrator}
                avatar={LESSONS[currentPhase.id].intro.avatar}
              >
                {typeof LESSONS[currentPhase.id].intro.text === 'function'
                  ? LESSONS[currentPhase.id].intro.text(playerName)
                  : LESSONS[currentPhase.id].intro.text}
              </NarratorDialog>
            )}
            
            {/* Chapters */}
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-amber-300">📚 Lições</h3>
              {LESSONS[currentPhase.id]?.chapters?.map((chapter, index) => {
                const isCompleted = gameData.completedChapters[`${currentPhase.id}-${chapter.id}`];
                const previousCompleted = index === 0 || gameData.completedChapters[`${currentPhase.id}-${LESSONS[currentPhase.id].chapters[index-1].id}`];
                
                return (
                  <ChapterCard
                    key={chapter.id}
                    chapter={chapter}
                    isCompleted={isCompleted}
                    isLocked={!previousCompleted}
                    onClick={() => {
                      if (previousCompleted) {
                        startChapter(chapter);
                      }
                    }}
                  />
                );
              })}
            </div>
            
            {/* Battle Buttons */}
            {LESSONS[currentPhase.id]?.chapters?.length > 0 && (() => {
              const lesson = LESSONS[currentPhase.id];
              const totalChapters = lesson?.chapters?.length || 0;
              const completedCount = getCompletedChaptersCount(currentPhase.id);
              const allDone = completedCount >= totalChapters && totalChapters > 0;
              return (
                <div className="mt-6 space-y-3">
                  <button
                    onClick={() => { playSound('click'); startBattle(); }}
                    className="w-full py-4 px-6 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white font-bold rounded-xl text-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-3"
                  >
                    <span className="text-2xl">⚔️</span>
                    BATALHAR - Testar Conhecimento
                  </button>
                  <button
                    onClick={() => { if (allDone) { playSound('click'); startBossMode(); } else { playSound('wrong'); } }}
                    className={`w-full py-4 px-6 rounded-xl text-lg font-bold transition-all duration-300 flex items-center justify-center gap-3 border-2 ${
                      allDone
                        ? 'bg-gradient-to-r from-purple-700 to-pink-600 hover:from-purple-600 hover:to-pink-500 border-purple-400 text-white hover:scale-[1.02] shadow-lg shadow-purple-500/30'
                        : 'bg-slate-800/50 border-slate-600/30 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    <span className="text-2xl">👑</span>
                    {allDone ? 'ENFRENTAR O BOSS' : `BOSS (complete todos os ${totalChapters} capítulos)`}
                  </button>
                  {!allDone && (
                    <p className="text-center text-slate-400 text-sm">
                      🔮 Dica: Complete as lições primeiro para desbloquear o Boss!
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ========== LESSON SCREEN ========== */}
        {screen === 'lesson' && currentChapter && currentChapter.pages && (
          <div className="space-y-4 animate-fadeIn">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => { playSound('click'); navigate('/fase'); }}
                className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
              >
                ← Voltar
              </button>
              <div className="text-sm text-slate-400">
                Página {currentPageIndex + 1} de {currentChapter.pages.length}
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden mb-6">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-yellow-400 transition-all duration-500"
                style={{ width: `${((currentPageIndex + 1) / currentChapter.pages.length) * 100}%` }}
              />
            </div>
            
            <LessonPage
              page={currentChapter.pages[currentPageIndex]}
              onNext={nextPage}
              onPracticeAnswer={handlePracticeAnswer}
              practiceState={practiceState}
            />
          </div>
        )}

        {/* ========== BATTLE SCREEN ========== */}
        {screen === 'battle' && currentEnemy && currentQuestion && (
          <div className="space-y-4 animate-fadeIn">
            {/* Header da batalha */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-400">{currentPhase?.icon} {currentPhase?.name}</div>
                <div className="text-amber-400 font-bold">+{battleXP} XP</div>
                {isBossMode && (
                  <div className="text-purple-300 text-xs font-bold mt-1">
                    👑 MODO BOSS — Pergunta {questionIndex + 1}/{battleQuestions.length}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasShield && (
                  <span className="text-2xl animate-pulse" title="Escudo ativo — próximo erro será absorvido!">🛡️</span>
                )}
                <HealthHearts lives={lives} />
              </div>
            </div>

            {/* Progresso do boss */}
            {isBossMode && (
              <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                  style={{ width: `${((questionIndex + 1) / battleQuestions.length) * 100}%` }}
                />
              </div>
            )}

            {/* Combo display */}
            {comboCount >= 2 && !isEnemyDefeated && (
              <ComboDisplay count={comboCount} hasShield={hasShield} />
            )}

            {/* Inimigo + dano flutuante */}
            <div className="py-4 relative">
              {damageFloat && (
                <DamageFloat
                  key={damageFloat.id}
                  value={damageFloat.value}
                  isCrit={damageFloat.isCrit}
                  onDone={() => setDamageFloat(null)}
                />
              )}
              <Enemy
                enemy={currentEnemy}
                currentHP={enemyHP}
                maxHP={currentEnemy.hp}
                isHit={isEnemyHit}
                isDefeated={isEnemyDefeated}
              />
            </div>

            {!isEnemyDefeated && (
              <BattleQuestion
                question={currentQuestion}
                onAnswer={handleBattleAnswer}
                selectedAnswer={selectedAnswer}
                isAnswered={isAnswered}
                showHint={showHint}
                onShowHint={() => setShowHint(true)}
              />
            )}

            {isAnswered && !isEnemyDefeated && lives > 0 && questionIndex < battleQuestions.length - 1 && (
              <button
                onClick={() => { playSound('click'); nextBattleQuestion(); }}
                className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold rounded-xl transition-all duration-300 hover:scale-[1.02]"
              >
                Próxima Questão →
              </button>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <LevelUpModal
        show={showLevelUp}
        newLevel={levelUpData.level}
        newTitle={levelUpData.title}
        onClose={() => {
          setShowLevelUp(false);
        }}
      />

      <VictoryModal
        show={showVictory}
        xpGained={battleXP}
        wrongTopics={wrongTopics}
        isBossMode={isBossMode}
        onClose={() => {
          setShowVictory(false);
          navigate('/fase');
        }}
      />

      <DefeatModal
        show={showDefeat}
        wrongTopics={wrongTopics}
        onRetry={() => {
          setShowDefeat(false);
          isBossMode ? startBossMode() : startBattle();
        }}
        onQuit={() => {
          setShowDefeat(false);
          navigate('/fase');
        }}
      />

      {/* Background Music */}
      <audio
        ref={audioRef}
        loop
        volume={0.3}
        preload="auto"
      >
        {/* Música personalizada do usuário */}
        <source src="/music/thema.mp3" type="audio/mpeg" />
      </audio>

      {/* Global Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap');
        * { font-family: 'Space Grotesk', sans-serif; }
      `}</style>
    </div>
  );
}